import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type {
  Control,
  FieldErrors,
  UseFormRegister,
  UseFormReturn,
} from 'react-hook-form';
import {
  Controller,
  FormProvider,
  useFieldArray,
  useForm,
  useWatch,
} from 'react-hook-form';
import { z } from 'zod';
import {
  Box,
  Button,
  DatePicker,
  Form,
  FormField,
  FormRow,
  FormSection,
  Heading,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  Select,
  Text,
  Tooltip,
} from '../ui';
import BaseContractFields, { baseContractFieldsSchema } from './BaseContract';
import ExtraFreightFields, { extraFreightFieldsSchema } from './ExtraFreights';
import ExtraCostFields, { extraCostFieldsSchema } from './ExtraCosts';
import CommissionFields, { commissionFieldsSchema } from './Commission';
import type {
  AllLocationsQuery,
  CargoUnit,
  Currency,
  IdRemappingInput,
  LocationByPortIdFragment,
  ScenarioTimeline_AssortmentFragment,
  ScenarioTimeline_OrderLocationFragment,
  VcAndOrdersMutationVariables,
  VoyageCharter_OrderFragment,
  VoyageCharter_PortDatumFragment,
  VoyageCharter_VoyageCharterFragment,
  VoyageCharterForm_AssortmentFragment,
  VoyageCharterForm_CargoUnitFragment,
  VoyageCharterForm_CoaAssortmentFragment,
  VoyageCharterForm_CoaFragment,
  VoyageCharterForm_CoaRouteFragment,
} from '../../graphql';
import {
  MarginUnit,
  PaymentUnit,
  useVoyageCharterFormQuery,
  useAllWorldscaleFreightsQuery,
} from '../../graphql';
import type { AutoCompleteOption } from '../AutoCompleteSelect';
import AutoCompleteSelect from '../AutoCompleteSelect';
import { compact, pickBy } from 'lodash';
import type { SelectOption } from '../ui/Select/Select';
import { paymentUnitsToOptions, reactSelectStyles } from '../../lib/form-util';
import { paymentUnitMap, paymentUnits } from '../../constants/paymentUnits';
import LocationSelect, {
  isLocationCoordinatesInvalid,
} from '../LocationSelect';
import {
  NONE,
  assertDefined,
  exhaustiveCheck,
  filteredMap,
  isDefined,
  mapKeyBy,
  withDefined,
  useDeepMemo,
} from '../../lib/util';
import ReactSelect from 'react-select';
import moment from 'moment-timezone';
import type {
  ContractDetailsSchema,
  VoyageCharterSchema,
  VoyageCharterSchemaInput,
} from './schema';
import { voyageCharterSchema } from './schema';
import type { ClientId } from '../core/ClientId';
import { generateClientId, isClientId } from '../core/ClientId';
import type { CargoRangeInputs, CargoRanges } from '../core/Capacity';
import { midPoint, calculateMassAndVolumeRanges } from '../core/Capacity';
import { msToH } from '../../lib/time';
import type { VoyageCharterOrderSchema } from './orders';
import { voyageCharterOrderSchema } from './orders';
import {
  valueOrNull,
  reactSelectOptionSchema,
  stringToNumber,
} from '../../lib/zod-util';
import { stringToNumOpt } from '../core/util';
import { zodResolver } from '@hookform/resolvers/zod';
import { useModal } from '../contexts/ModalContext';
import {
  FALLBACK_COA_CARGO_UNIT,
  getOrderCoaDetails,
} from '../shipment/getOderCoaDetails';
import { DEFAULT_LAYCAN_DAYS, getFreights } from './voyageCharter';
import { useCurrentUser, useDefaultCurrency } from '../../state/zustand';
import { reducedUnits } from '../../constants/cargoUnits';
import {
  CalculationBox,
  cargoExclusionTypeOptions,
  DOWNCARET,
  formatLoadingRateOverrideTooltip,
  UPCARET,
} from '../order/OrderForm';
import { marginUnits } from '../../constants/margin';
import { imoClassOptions } from '../../constants/imoClassNames';
import {
  CargoExclusionType,
  getCargoExclusionType,
  getCargoExclusionValue,
} from '../order/cargoExclusion';
import loadingUnitOptions, {
  loadingRateSourceToLabel,
  loadingUnitToName,
} from '../../constants/loadingUnits';
import useEvent from '../hooks/useEvent';
import { OrderEmailInfo } from './VoyageCharterForm.OrderEmailInfo';
import LoadingSpinner from '../LoadingSpinner';
import {
  FormSettingsDropdown,
  FormSettingsProvider,
  FormVisibilityBox,
} from '../ui/Form/FormSettings';
import PortDataForm, {
  getPrefixedNumberKey,
  OverrideTooltip,
  parsePrefixedNumberKey,
  portDataNodesToPortData,
  portDataSchema,
} from '../contracts/PortDataForm';
import { AlertIcon } from '../Icons';
import { useAllLocations } from '../../lib/allLocations';
import { useDialog } from '../ui/Modal/useDialog';
import { isAssortmentValid } from '../order/OrderTableUtils';
import { formatCurrency } from '../../lib/format';
import { DefaultMap } from '@seaber/common/lib/collections';
import { timeFieldFormat } from '../ui/TimeField';
import type {
  EffectiveLoadingRate,
  GetEffectiveLoadingRateParams,
} from '../core/Core.loading-discharging';
import {
  getEffectiveLoadingRate,
  getLoadingTime,
} from '../core/Core.loading-discharging';
import { getFreightCost } from '../shipment/costs/freightCost';
import type {
  OrderPorts,
  WorldscaleFreightRateMap,
} from '../../lib/worldscaleFreight';
import { buildWorldscaleFreightRateMap } from '../../lib/worldscaleFreight';

export const relatedOrderSchema = z
  .object({
    order: voyageCharterOrderSchema.extend({
      loadPortId: reactSelectOptionSchema,
      dischargePortId: reactSelectOptionSchema,
    }),
    relationId: z.number().nullable(),
    coaRouteId: reactSelectOptionSchema.nullable(),
    freightId: z.number().nullable(),
    freightAmount: z.number().nullable(),
    freightUnit: z.nativeEnum(PaymentUnit).nullable(),
  })
  .refine(
    (data) => {
      if (isDefined(data.freightAmount)) {
        return isDefined(data.freightUnit);
      }
      return true;
    },
    {
      message: 'freightUnit is required if freightAmount is not empty',
      path: ['freightUnit'],
    },
  );

export type RelatedOrderSchema = z.infer<typeof relatedOrderSchema>;

const voyageCharterFormSchema = baseContractFieldsSchema
  .merge(commissionFieldsSchema)
  .merge(extraFreightFieldsSchema)
  .merge(extraCostFieldsSchema)
  .extend({
    portData: portDataSchema,
    relatedOrders: z.array(relatedOrderSchema).min(1),
  })
  .refine(
    (data) => {
      const hasFreightAmount = data.relatedOrders.some((order) => {
        return isDefined(order.freightAmount);
      });

      if (hasFreightAmount) {
        return isDefined(data.currency);
      }
      return true;
    },
    {
      message: 'currency is required if freightAmount is not empty',
      path: ['currency'],
    },
  );

export type VoyageCharterFormSchema = z.infer<typeof voyageCharterFormSchema>;

interface VoyageCharterFormProps {
  isSaveLoading: boolean;
  onClose: () => void;
  onDelete: () => void;
  onSave: (
    voyageCharter: VoyageCharterSchema,
    orders: VoyageCharterOrderSchema[],
    synonymMappings: Pick<
      VcAndOrdersMutationVariables,
      'mapAssortments' | 'mapLocations'
    >,
    opts: {
      shouldClose: boolean;
      showSuggestions?: boolean;
    },
  ) => void;
  persistedValues: VoyageCharterFormPersistedValues | null;
  title?: string;
  orders: VoyageCharter_OrderFragment[];
}

interface VoyageCharterFormPersistedValues {
  contract: ContractDetailsSchema;
  timezone: string | null;
  voyageCharterId: number | null;
  voyageCharter: VoyageCharterFormSchema;
  orders: VoyageCharter_OrderFragment[];
  duplicatedOrderIdByOriginalId: Map<number, ClientId>;
}

export function useVoyageCharterFormPersistedValues(
  vc: VoyageCharter_VoyageCharterFragment | null,
  opts: { duplicate: boolean },
): VoyageCharterFormPersistedValues | null {
  return useDeepMemo(
    useMemo(() => getVoyageCharterFormPersistedValues(vc, opts), [opts, vc]),
  );
}

export function getVoyageCharterFormPersistedValues(
  vc: VoyageCharter_VoyageCharterFragment | null,
  opts: { duplicate: boolean },
): VoyageCharterFormPersistedValues | null {
  if (!vc) return null;
  const duplicatedOrderIdByOriginalId = new Map<number, ClientId>();

  const contract = getDefaultContract(vc);

  if (opts.duplicate && contract.type === 'spot') {
    contract.onSubsUntil = null;
  }

  const timezone = getDefaultTimezone(vc);

  const orders = compact(
    vc.orderRelations.nodes.map((orderRel) => orderRel?.orderByOrderId),
  );

  const relatedOrders: VoyageCharterFormSchema['relatedOrders'] = compact(
    vc.orderRelations.nodes,
  )
    .sort((a, b) => a.id - b.id)
    .map((orderRel) => {
      const order = orderRel.orderByOrderId;
      const freight = orderRel.freight.nodes[0] ?? null;

      const cargoExclusions =
        order?.cargoExclusionsByOrderId?.nodes
          .map((c) => c?.assortmentGroupByAssortmentGroup)
          .filter(isDefined)
          .map((a) => ({ value: a.id, label: a.name })) ?? [];

      const firstCargoExclusion = order?.cargoExclusionsByOrderId?.nodes?.at(0);

      assertDefined(order, 'order');

      const loadPort = order.locationByLoadPortId;
      const dischargePort = order.locationByDischargePortId;
      const coaRoute = orderRel.coaContractPortByCoaRouteId;
      const orderDetails = order.orderDetailsByOrderId.nodes[0];
      const newId = opts.duplicate ? generateClientId() : order.id;
      const earliestLoading = order.layday;
      const latestLoading = order.cancelling;
      const earliestDischarging = order.rta;
      const latestDischarging = order.deliveredBefore;

      if (isClientId(newId)) {
        duplicatedOrderIdByOriginalId.set(order.id, newId);
      }
      return {
        order: {
          id: newId,
          loadPortId: loadPort
            ? { value: loadPort.id, label: loadPort.name ?? '' }
            : null,
          dischargePortId: dischargePort
            ? { value: dischargePort.id, label: dischargePort.name ?? '' }
            : null,
          cargoAmount: +order.cargoAmount,
          cargoUnitId: order.cargoUnitByCargoUnitId?.id ?? null,
          assortmentId: order.assortmentId ?? NaN,
          extraInfo: orderDetails?.extraInfo ?? null,
          orderName: opts.duplicate ? null : (orderDetails?.orderName ?? null),
          fixtureNote: opts.duplicate
            ? null
            : (orderDetails?.fixtureNote ?? null),
          cargoDeckOption: orderDetails?.cargoDeckOption ?? null,
          externalOrderId: opts.duplicate
            ? null
            : (order?.externalOrderId ?? null),
          loadingRate: stringToNumOpt(order?.loadingRate),
          loadingUnit: order?.loadingUnit ?? undefined,
          dischargingRate: stringToNumOpt(order?.dischargingRate),
          dischargingUnit: order?.dischargingUnit ?? undefined,
          layday: earliestLoading ? new Date(earliestLoading) : null,
          cancelling: latestLoading ? new Date(latestLoading) : null,
          rta: earliestDischarging ? new Date(earliestDischarging) : null,
          deliveredBefore: latestDischarging
            ? new Date(latestDischarging)
            : null,
          loadingOverheadDuration: order?.loadingOverheadDuration
            ? stringToNumOpt(
                msToH(+Number(order.loadingOverheadDuration)).toFixed(2),
              )
            : null,
          dischargingOverheadDuration: order?.dischargingOverheadDuration
            ? stringToNumOpt(
                msToH(+Number(order.dischargingOverheadDuration)).toFixed(2),
              )
            : null,
          assortmentOptions: {
            stowageFactor:
              order?.stowageFactorMin || order?.stowageFactorMax
                ? {
                    min: stringToNumOpt(order.stowageFactorMin) ?? null,
                    max: stringToNumOpt(order.stowageFactorMax) ?? null,
                  }
                : undefined,
            imoClass: orderDetails?.imoClass ?? null,
            specificGravity:
              withDefined(order?.specificGravity, (x) => +x) ?? null,
            marginAmount: withDefined(order?.marginAmount, (x) => +x) ?? null,
            marginUnit: order?.marginUnit ?? MarginUnit.PERCENTAGE,
            cargoExclusions,
            cargoExclusionAmount:
              (firstCargoExclusion &&
                getCargoExclusionValue(firstCargoExclusion)) ??
              0,
            cargoExclusionType:
              (firstCargoExclusion &&
                getCargoExclusionType(firstCargoExclusion)) ??
              CargoExclusionType.voyages,
          },
        },
        relationId: opts.duplicate ? null : orderRel.id,
        coaRouteId: coaRoute ? getCoaRouteOption(coaRoute) : null,
        freightId: opts.duplicate ? null : (freight?.id ?? null),
        freightAmount: stringToNumOpt(freight?.freightAmount) ?? null,
        freightUnit: freight?.freightUnit ?? null,
      };
    });

  const commissions = compact(vc?.commissionsByVoyageCharterId.nodes).map(
    (cm) => ({
      id: opts.duplicate ? null : cm.id,
      type: cm.type,
      value: +cm.value,
      demurrageApplied: cm.demurrageApplied ?? null,
      extraFreightApplied: cm.extraFreightApplied ?? null,
      bafApplied: cm.bafApplied ?? null,
      organizationId: {
        value: cm.organizationId ?? 0,
        label: cm.organizationByOrganizationId?.name ?? '',
      },
      loadPortId: cm.loadPortId ?? null,
      dischargePortId: cm.dischargePortId ?? null,
      assortmentId: cm.assortmentId ?? null,
      coaRouteId: null,
    }),
  );

  const extraFreights = compact(
    vc?.voyageCharterExtraFreightsByVoyageCharterId.nodes,
  ).map((ef) => ({
    id: opts.duplicate ? null : ef.id,
    name: ef.name ?? null,
    amount: ef.amount.toString(),
    description: ef.description ?? null,
    type: ef.type ?? null,
    ignoreInPnl: ef.ignoreInPnl ?? null,
  }));

  const extraCosts = compact(
    vc?.voyageCharterExtraCostsByVoyageCharterId.nodes,
  ).map((ec) => ({
    id: opts.duplicate ? null : ec.id,
    name: ec.name ?? null,
    amount: ec.amount.toString(),
    description: ec.description ?? null,
    type: ec.type ?? null,
  }));

  const portData = portDataNodesToPortData(
    compact(
      vc?.voyageCharterPortDataByVoyageCharterId.nodes.map(
        (pdRel) => pdRel?.portDatumByPortDataId,
      ),
    ),
  );

  const voyageCharter: VoyageCharterFormSchema = {
    chartererOrgId: vc.chartererOrgId
      ? {
          value: vc.chartererOrgId,
          label: vc.organizationByChartererOrgId?.name ?? '',
        }
      : null,
    shipownerOrgId: vc.shipownerOrgId
      ? {
          value: vc.shipownerOrgId,
          label: vc.organizationByShipownerOrgId?.name ?? '',
        }
      : null,
    vesselId: vc?.vesselId
      ? {
          value: vc.vesselId,
          label: vc.shipByVesselId?.name ?? '',
        }
      : null,
    demurrage: vc.demurrage ? +vc.demurrage : null,
    paymentTrigger: vc.paymentTrigger ?? null,
    currency: vc.currency ?? null,
    name: vc.name ?? null,
    lumpsum: vc.lumpsum ? +vc.lumpsum : null,
    externalId: opts.duplicate ? null : (vc.externalId ?? null),
    paymentType: vc.paymentType ?? null,
    paymentTime: vc.paymentTime ?? null,
    layday: vc.layday ? new Date(vc.layday) : null,
    cancelling: vc.cancelling ? new Date(vc.cancelling) : null,
    relatedOrders,
    commissions,
    extraFreights,
    extraCosts,
    portData,
  };

  const voyageCharterId = opts.duplicate ? null : vc.id;

  return {
    contract,
    timezone,
    voyageCharter,
    orders,
    voyageCharterId,
    duplicatedOrderIdByOriginalId,
  };
}

function getDefaultOrder(defaults: {
  assortmentId: RelatedOrderSchema['order']['assortmentId'];
  cargoUnitId: RelatedOrderSchema['order']['cargoUnitId'];
}): RelatedOrderSchema {
  return {
    order: {
      id: generateClientId(),
      assortmentId: defaults.assortmentId,
      cargoAmount: NaN,
      cargoUnitId: defaults.cargoUnitId,
      loadPortId: null,
      dischargePortId: null,
      extraInfo: null,
      orderName: null,
      fixtureNote: null,
      cargoDeckOption: null,
      externalOrderId: null,
      loadingRate: null,
      dischargingRate: null,
      layday: null,
      cancelling: null,
      rta: null,
      deliveredBefore: null,
      loadingOverheadDuration: null,
      dischargingOverheadDuration: null,
      loadingUnit: undefined,
      dischargingUnit: undefined,
      assortmentOptions: null,
    },
    relationId: null,
    coaRouteId: null,
    freightId: null,
    freightAmount: null,
    freightUnit: null,
  };
}

function getDefaultValues(defaults: {
  assortmentId: RelatedOrderSchema['order']['assortmentId'];
  cargoUnitId: RelatedOrderSchema['order']['cargoUnitId'];
  currency: VoyageCharterFormSchema['currency'];
  shipownerOrgId: VoyageCharterFormSchema['shipownerOrgId'];
}): VoyageCharterFormSchema {
  const relatedOrders = [
    getDefaultOrder({
      assortmentId: defaults.assortmentId,
      cargoUnitId: defaults.cargoUnitId,
    }),
  ];

  return {
    chartererOrgId: null,
    shipownerOrgId: defaults.shipownerOrgId,
    vesselId: null,
    demurrage: null,
    paymentTrigger: null,
    currency: defaults.currency,
    name: null,
    lumpsum: null,
    externalId: null,
    paymentType: null,
    paymentTime: null,
    layday: null,
    cancelling: null,
    relatedOrders,
    commissions: [],
    extraFreights: [],
    extraCosts: [],
    portData: {},
  };
}

function getDefaultContract(
  voyageCharter: VoyageCharter_VoyageCharterFragment | null,
): ContractDetailsSchema {
  if (voyageCharter === null || voyageCharter.isInquiry) {
    return { type: 'inquiry' };
  }

  const coaId =
    voyageCharter.voyageCharterToContractsByVoyageCharterId.nodes[0]?.coaId ??
    null;

  if (coaId !== null) {
    return {
      type: 'coa',
      coaId,
      isPreliminary: voyageCharter.isPreliminary,
    };
  }

  return {
    type: 'spot',
    onSubsUntil:
      voyageCharter.onSubsUntil != null
        ? new Date(voyageCharter.onSubsUntil).toISOString()
        : null,
  };
}

function useVoyageCharterFormDefaultValues(
  voyageCharter: VoyageCharterFormSchema | null,
  assortments: VoyageCharterForm_AssortmentFragment[],
  cargoUnits: VoyageCharterForm_CargoUnitFragment[],
): {
  defaultCargoUnitId: number | null;
  defaultValues: VoyageCharterFormSchema;
  isDefaultLaycanEnabled: boolean;
} {
  const currentUser = useCurrentUser();
  const defaultCurrency = useDefaultCurrency();
  const currentOrg = currentUser?.organization;

  const defaultAssortmentId =
    assortments.sort((a, b) => a.name.localeCompare(b.name))[0]?.id ?? null;

  const defaultCargoUnitId =
    cargoUnits.find((unit) => unit.name === 'mts')?.id ?? null;

  const defaultValues = useMemo(() => {
    const defaultShipowner = currentOrg?.shipOwner
      ? {
          value: currentOrg.id,
          label: currentOrg.name ?? '',
        }
      : null;

    return (
      voyageCharter ||
      getDefaultValues({
        assortmentId: defaultAssortmentId,
        cargoUnitId: defaultCargoUnitId,
        currency: defaultCurrency,
        shipownerOrgId: defaultShipowner,
      })
    );
  }, [
    currentOrg?.id,
    currentOrg?.name,
    currentOrg?.shipOwner,
    defaultAssortmentId,
    defaultCargoUnitId,
    defaultCurrency,
    voyageCharter,
  ]);

  const isDefaultLaycanEnabled =
    voyageCharter === null &&
    !!currentUser?.organization?.configEnableDefaultLaycan;

  return { defaultValues, defaultCargoUnitId, isDefaultLaycanEnabled };
}

function getDefaultTimezone(
  voyageCharter: VoyageCharter_VoyageCharterFragment | null,
): string | null {
  // Use load port time zone of the order with the lowest relation id
  return (
    compact(voyageCharter?.orderRelations.nodes).sort((a, b) => a.id - b.id)[0]
      ?.orderByOrderId?.locationByLoadPortId?.timezone ?? null
  );
}

type UseSynonymMappingsOrder = {
  loadPortId?: number | null;
  dischargePortId?: number | null;
  assortmentId?: number | null;
};

type UseSynonymMappingsOrderWithData = {
  locationByLoadPortId?: ScenarioTimeline_OrderLocationFragment | null;
  locationByDischargePortId?: ScenarioTimeline_OrderLocationFragment | null;
  assortmentByAssortmentId?: ScenarioTimeline_AssortmentFragment | null;
};

/** Returned function takes in old-new order pairs and sees if any locations or assortments should be sent to backend for being mapped as synonyms. */
export function useGetSynonymMappings(
  allLocations: ScenarioTimeline_OrderLocationFragment[],
  allAssortments: VoyageCharterForm_AssortmentFragment[],
) {
  const getConfirmSynonymResult = useDialog(
    (
      { from, to }: { from: string; to: string },
      onFinish: (result: boolean) => void,
    ) => (
      <Modal
        isOpen={true}
        onClose={() => onFinish(false)}
        width="420px"
        height="160px"
      >
        <ModalBody overflowY="auto">
          <Text>
            Save {from} as a permanent synonym for {to} and change {from} to{' '}
            {to} for all existing orders?
          </Text>
        </ModalBody>
        <ModalFooter>
          <Button onPress={() => onFinish(false)} mr={3}>
            No
          </Button>
          <Button variant="positive" onPress={() => onFinish(true)}>
            Save
          </Button>
        </ModalFooter>
      </Modal>
    ),
  );

  return useEvent(
    async (
      orderPairs: [
        oldOrder: UseSynonymMappingsOrderWithData,
        newOrder: UseSynonymMappingsOrder,
      ][],
    ) => {
      const locationsById = mapKeyBy(allLocations, (l) => l.id);

      const mapLocations: IdRemappingInput[] = [];

      const handlePortChange = async (
        currentLocation: LocationByPortIdFragment | undefined | null,
        newPortId: number | undefined | null,
      ) => {
        const newPortName =
          (isDefined(newPortId) && locationsById.get(newPortId)?.name) ||
          'another port';
        const canApplySynonym =
          isDefined(newPortId) &&
          currentLocation &&
          isLocationCoordinatesInvalid(currentLocation) &&
          currentLocation.id !== newPortId;

        if (
          canApplySynonym &&
          (await getConfirmSynonymResult({
            from: currentLocation?.name,
            to: newPortName,
          }))
        ) {
          // Save as synonym, backend changes all references to this location to the new one and deletes the old row
          mapLocations.push({
            from: currentLocation.id,
            to: newPortId,
          });
        }
      };

      const assortmentsById = mapKeyBy(allAssortments, (a) => a.id);

      const mapAssortments: IdRemappingInput[] = [];

      const handleAssortmentChange = async (
        currentAssortment:
          | ScenarioTimeline_AssortmentFragment
          | undefined
          | null,
        newAssortmentId: number | undefined | null,
      ) => {
        const newAssortmentName =
          (isDefined(newAssortmentId) &&
            assortmentsById.get(newAssortmentId)?.name) ||
          'another assortment';
        const canApplySynonym =
          isDefined(newAssortmentId) &&
          currentAssortment &&
          !isAssortmentValid(currentAssortment) &&
          currentAssortment.id !== newAssortmentId;
        if (
          canApplySynonym &&
          (await getConfirmSynonymResult({
            from: currentAssortment?.name,
            to: newAssortmentName,
          }))
        ) {
          // Save as synonym, backend changes all references to this location to the new one and deletes the old row
          mapAssortments.push({
            from: currentAssortment.id,
            to: newAssortmentId,
          });
        }
      };

      for (const [oldOrder, newOrder] of orderPairs) {
        if (isDefined(oldOrder.locationByLoadPortId)) {
          await handlePortChange(
            oldOrder.locationByLoadPortId,
            newOrder.loadPortId,
          );
        }
        if (isDefined(oldOrder.locationByDischargePortId)) {
          await handlePortChange(
            oldOrder.locationByDischargePortId,
            newOrder.dischargePortId,
          );
        }
        if (isDefined(oldOrder.assortmentByAssortmentId)) {
          await handleAssortmentChange(
            oldOrder.assortmentByAssortmentId,
            newOrder.assortmentId,
          );
        }
      }

      return { mapLocations, mapAssortments };
    },
  );
}

type VoyageCharterFormContractDetails =
  | ContractDetailsSchema
  | { type: 'coa'; coaId: null; isPreliminary: boolean };

type CoaValues = {
  coaPortDataByLocationId: Map<number, VoyageCharter_PortDatumFragment>;
};

export default function VoyageCharterForm({
  isSaveLoading,
  onClose,
  onDelete,
  onSave,
  persistedValues,
  title,
  orders,
}: VoyageCharterFormProps): JSX.Element {
  const { data, refetch, loading } = useVoyageCharterFormQuery();
  const [formValidationErrors, setFormValidationErrors] = useState<string[]>(
    [],
  );

  const [submitValidationErrors, setSubmitValidationErrors] = useState<
    string[]
  >([]);
  const validationErrors = [...formValidationErrors, ...submitValidationErrors];
  const currentUser = useCurrentUser();
  const shouldUseReducedUnits =
    currentUser?.rootOrganization?.configReducedUnits ?? false;

  const cargoUnits = useMemo(
    () =>
      compact(
        data?.allCargoUnits?.nodes.filter((unit) => {
          if (shouldUseReducedUnits) {
            return reducedUnits.includes(unit?.name ?? '');
          }
          return unit;
        }),
      ),
    [data?.allCargoUnits?.nodes, shouldUseReducedUnits],
  );

  const assortments = useMemo(
    () => compact(data?.allAssortments?.nodes),
    [data?.allAssortments?.nodes],
  );
  const charterers = useMemo(
    () => compact(data?.charterers?.nodes),
    [data?.charterers?.nodes],
  );
  const shipowners = useMemo(
    () => compact(data?.shipowners?.nodes),
    [data?.shipowners?.nodes],
  );
  const organizations = useMemo(
    () => compact(data?.allOrganizations?.nodes),
    [data?.allOrganizations?.nodes],
  );
  const coas = compact(
    data?.allCoaContracts?.nodes.filter((coa) => !coa?.inactive),
  );

  const [contract, setContract] = useState<VoyageCharterFormContractDetails>(
    persistedValues?.contract ?? { type: 'inquiry' },
  );

  const isCoa = contract.type === 'coa';
  const coaId = isCoa ? contract.coaId : null;
  const selectedCoa = useMemo(
    () =>
      coaId !== null ? (coas.find((coa) => coa.id === coaId) ?? null) : null,
    [coas, coaId],
  );

  const coaRoutes = useMemo(
    () =>
      compact(selectedCoa?.coaContractPortsByCoaContractId?.nodes).map(
        (route) => ({
          ...route,
          originId: route.originId ?? null,
          originZoneId: route.originZoneId ?? null,
          destinationId: route.destinationId ?? null,
          destinationZoneId: route.destinationZoneId ?? null,
          destinationZoneLocations: new Set(
            compact(
              route.zoneByDestinationZoneId?.zoneLocationsByZoneId.nodes,
            ).map((zoneLocation) => zoneLocation.locationId),
          ),
          originZoneLocations: new Set(
            compact(route.zoneByOriginZoneId?.zoneLocationsByZoneId.nodes).map(
              (zoneLocation) => zoneLocation.locationId,
            ),
          ),
        }),
      ),
    [selectedCoa],
  );

  const inheritedCoaValues: CoaValues | null = useMemo(() => {
    if (!selectedCoa) return null;

    const coaPortDataByLocationId: Map<
      number,
      VoyageCharter_PortDatumFragment
    > = new Map();

    for (const portDataRel of selectedCoa?.coaContractPortDataByCoaContractId
      .nodes ?? []) {
      const portData = portDataRel?.portDatumByPortDataId;
      if (!portData) continue;

      coaPortDataByLocationId.set(portData.portId, portData);
    }

    return {
      coaPortDataByLocationId,
    };
  }, [selectedCoa]);

  const { defaultCargoUnitId, defaultValues, isDefaultLaycanEnabled } =
    useVoyageCharterFormDefaultValues(
      persistedValues?.voyageCharter ?? null,
      assortments,
      cargoUnits,
    );

  const [timezone, setTimezone] = useState<string>(
    persistedValues?.timezone ?? 'UTC',
  );

  const form = useForm<VoyageCharterFormSchema>({
    defaultValues,
    resolver: zodResolver(voyageCharterFormSchema),
  });
  const { getValues, handleSubmit, setValue, watch, reset } = form;

  useEffect(() => {
    reset(defaultValues);
  }, [reset, defaultValues]);

  useEffect(() => {
    setContract(persistedValues?.contract ?? { type: 'inquiry' });
  }, [persistedValues?.contract]);

  useEffect(() => {
    setTimezone(persistedValues?.timezone ?? 'UTC');
  }, [persistedValues?.timezone]);

  const watchedShipownerOrgId = watch('shipownerOrgId');
  const selectedShipowner = watchedShipownerOrgId?.value
    ? shipowners.find(
        (shipowner) => shipowner.id == watchedShipownerOrgId.value,
      )
    : undefined;
  const vessels = compact(selectedShipowner?.shipsByOrganizationId?.nodes);

  const coaAssortments = compact(
    selectedCoa?.coaContractAssortmentsByCoaContractId?.nodes,
  );

  const coaFreights = compact(
    selectedCoa?.coaContractPricesByCoaContractId?.nodes,
  );

  const coaCommissions = useMemo(
    () => compact(selectedCoa?.coaContractCommissionsByCoaContractId.nodes),
    [selectedCoa],
  );

  const coaCommissionAssortments = new Set(
    compact(
      selectedCoa?.coaContractCommissionsByCoaContractId.nodes.map(
        (i) => i?.coaContractAssortmentByCoaAssortmentId?.assortmentId,
      ),
    ),
  );

  const coaVesselIds = new Set(
    compact(
      selectedCoa?.coaContractShipsByCoaContractId?.nodes.map((s) => s?.shipId),
    ),
  );

  const defaultCurrency = useDefaultCurrency();
  const getCoaFreight: GetCoaFreight = useCallback(
    (relatedOrder) => {
      const { order } = relatedOrder;

      const assortment = assortments.find(
        (assortment) => assortment.id === order.assortmentId,
      );

      const cargoUnit = cargoUnits.find(
        (unit) => unit.id === order.cargoUnitId,
      );

      if (!cargoUnit || !isDefined(order.cargoAmount)) {
        return { result: null, errors: null };
      }

      const cargoRanges = calculateMassAndVolumeRanges(
        {
          cargoUnitByCargoUnitId: cargoUnit,
          cargoAmount: order.cargoAmount,
          marginAmount: 0,
          marginUnit: null,
          specificGravity: assortment?.specificGravity ?? null,
          stowageFactorMin: assortment?.stowageFactorMin ?? null,
          stowageFactorMax: assortment?.stowageFactorMax ?? null,
        },
        false,
      );

      const { coaAssortment, coaFreight, errors } = getOrderCoaDetails(
        {
          assortmentId: assortment?.id ?? null,
          assortmentGroupId: assortment?.assortmentGroup ?? null,
          loadPortId: order.loadPortId?.value ?? null,
          dischargePortId: order.dischargePortId?.value ?? null,
        },
        {
          mass: midPoint(cargoRanges.mass),
          volume: midPoint(cargoRanges.volume),
        },
        {
          cargoUnitName: selectedCoa?.cargoUnitByAmountUnitId?.name ?? null,
          coaAssortments: coaAssortments.map((assortment) => ({
            ...assortment,
            assortmentId: assortment.assortmentId ?? null,
            assortmentGroupId: assortment.assortmentGroupId ?? null,
          })),
          routes: coaRoutes,
          freights: coaFreights,
        },
        relatedOrder.coaRouteId?.value ?? null,
      );

      return coaFreight && coaAssortment
        ? {
            result: {
              amount: +coaFreight.price,
              unit: coaAssortment.paymentUnit ?? null,
              currency: selectedCoa?.currency ?? defaultCurrency,
            },
            errors: null,
          }
        : {
            result: null,
            errors: {
              invalidAmount: errors.invalidAmount,
            },
          };
    },
    [
      assortments,
      cargoUnits,
      coaAssortments,
      coaFreights,
      coaRoutes,
      defaultCurrency,
      selectedCoa?.cargoUnitByAmountUnitId?.name,
      selectedCoa?.currency,
    ],
  );

  const coaAssortmentOptions = useMemo(() => {
    const coaAssortmentsByAssortmentId = new DefaultMap<
      number,
      VoyageCharterForm_CoaAssortmentFragment[]
    >(() => []);
    const coaAssortmentsByAssortmentGroupId = new DefaultMap<
      number,
      VoyageCharterForm_CoaAssortmentFragment[]
    >(() => []);

    const arr: CoaAssortmentOption[] = [];

    coaAssortments.forEach((ca) => {
      if (ca.assortmentId) {
        coaAssortmentsByAssortmentId.set(ca.assortmentId, [
          ...coaAssortmentsByAssortmentId.get(ca.assortmentId),
          ca,
        ]);
      }

      if (ca.assortmentGroupId) {
        coaAssortmentsByAssortmentGroupId.set(ca.assortmentGroupId, [
          ...coaAssortmentsByAssortmentGroupId.get(ca.assortmentGroupId),
          ca,
        ]);
      }
    });

    assortments.forEach((assortment) => {
      const byAssortmentId = coaAssortmentsByAssortmentId.get(assortment.id);
      const byGroupId = assortment.assortmentGroup
        ? coaAssortmentsByAssortmentGroupId.get(assortment.assortmentGroup)
        : [];

      if (byAssortmentId.length < 1 && byGroupId.length < 1) return;

      const amountRanges = [...byAssortmentId, ...byGroupId].map(
        (coaAssortment) => ({
          min: stringToNumOpt(coaAssortment.minAmount) ?? null,
          max: stringToNumOpt(coaAssortment.maxAmount) ?? null,
        }),
      );

      arr.push({
        label: assortment.name,
        value: assortment.id,
        amountRanges,
        isStowageFactor:
          !!assortment.stowageFactorMin || !!assortment.stowageFactorMax,
      });
    });

    return arr.sort((a, b) => a.label.localeCompare(b.label));
  }, [assortments, coaAssortments]);

  const filterCommissionAssortments =
    coaId !== null
      ? assortments.filter((a) => coaCommissionAssortments.has(a.id))
      : assortments;

  const filteredVessels =
    coaId !== null ? vessels.filter((v) => coaVesselIds.has(v.id)) : vessels;

  const [freightUnitErrorMessage, setFreightUnitErrorMessage] = useState<
    string | null
  >(null);
  const [currencyErrorMessage, setCurrencyErrorMessage] = useState<
    string | null
  >(null);
  const [errorMessage, setErrorMessage] = useState('');
  const formRef = useRef<HTMLFormElement | null>(null);

  const { data: allLocationsData } = useAllLocations();

  const getSynonymMappings = useGetSynonymMappings(
    compact(allLocationsData?.allLocations?.nodes),
    assortments,
  );

  const oninvalid = (errors: FieldErrors<VoyageCharterFormSchema>) => {
    const formErrors: string[] = [];
    setSubmitValidationErrors([]);

    const { relatedOrders, currency } = errors;

    if (currency) {
      formErrors.push(
        `Please specify a currency when providing a freight amount.`,
      );
      setCurrencyErrorMessage(
        'Please specify a currency when providing a freight amount',
      );
    }

    if (relatedOrders) {
      relatedOrders?.forEach?.((item, index) => {
        if (!item) return;

        if (item.order) {
          if (item.order.cargoAmount) {
            formErrors.push(
              `Please specify a quantity in the order №${index + 1}.`,
            );
          }
          if (item.order.loadPortId) {
            formErrors.push(
              `Please specify a load port in the order №${index + 1}.`,
            );
          }
          if (item.order.dischargePortId) {
            formErrors.push(
              `Please specify a discharge port in the order №${index + 1}.`,
            );
          }
        }

        if (item.freightUnit) {
          formErrors.push(
            `Please specify a freight unit when providing a freight amount in the freight №${
              index + 1
            }.`,
          );
          setFreightUnitErrorMessage(
            'Please specify a freight unit when providing a freight amount',
          );
        }
      });
    }

    setFormValidationErrors(formErrors);
  };

  const onSubmit =
    (opts: { shouldClose: boolean; showSuggestions?: boolean }) =>
    async (formData: VoyageCharterFormSchema) => {
      const formErrors: string[] = [];
      if (contract.type === 'coa' && contract.coaId === null) {
        return;
      }

      formData.relatedOrders.forEach((item, index) => {
        if (
          item.order.layday &&
          item.order.cancelling &&
          item.order.layday > item.order.cancelling
        ) {
          formErrors.push(
            `Loading window end needs to be later than start in the order №${index + 1}`,
          );
        }
        if (
          item.order.rta &&
          item.order.deliveredBefore &&
          item.order.rta > item.order.deliveredBefore
        ) {
          formErrors.push(
            `Discharge window end needs to be later than start in the order №${index + 1}.`,
          );
        }
        if (
          item.order.layday &&
          item.order.rta &&
          item.order.layday > item.order.rta
        ) {
          formErrors.push(
            `Discharge window start needs to be later than loading window start in the order №${index + 1}. Proceed with caution.`,
          );
        }
        if (
          item.order.layday &&
          item.order.deliveredBefore &&
          item.order.layday > item.order.deliveredBefore
        ) {
          formErrors.push(
            `Discharge window end needs to be later than loading window start in the order №${index + 1}.`,
          );
        }
      });
      setSubmitValidationErrors(formErrors);
      if (formErrors.length > 0) {
        return;
      }

      setErrorMessage('');
      if (!formRef.current?.checkValidity()) {
        return;
      }

      const {
        extraFreights,

        commissions,
        chartererOrgId,
        shipownerOrgId,
        vesselId,
        layday,
        cancelling,
        relatedOrders,
        extraCosts,
        portData,
        ...baseContract
      } = formData;

      try {
        const formDataToParse: VoyageCharterSchemaInput = {
          id: persistedValues?.voyageCharterId ?? null,
          contractDetails: contract,
          baseContract: {
            ...baseContract,
            chartererOrgId: chartererOrgId?.value ?? null,
            shipownerOrgId: shipownerOrgId?.value ?? null,
            vesselId: vesselId?.value ?? null,
            layday: layday?.toISOString() ?? null,
            cancelling: cancelling?.toISOString() ?? null,
          },
          relatedOrders: relatedOrders.map(({ order, ...relatedOrder }) => ({
            ...relatedOrder,
            freightAmount: !baseContract.lumpsum
              ? relatedOrder.freightAmount
              : null,
            orderId: order.id,
            coaRouteId: relatedOrder.coaRouteId?.value ?? null,
            layday: order.layday?.toISOString() ?? null,
            cancelling: order.cancelling?.toISOString() ?? null,
          })),
          commissions: commissions.map((commission) => ({
            ...commission,
            organizationId: commission.organizationId?.value ?? null,
          })),
          extraFreights,
          extraCosts,
          portData: removeLeftoverPortData(portData, relatedOrders),
        };

        const parsedVoyageCharter = voyageCharterSchema.parse(formDataToParse);
        const parsedOrders = relatedOrders.map(({ order }) =>
          voyageCharterOrderSchema.parse({
            ...order,
            loadPortId: order.loadPortId?.value ?? NaN,
            dischargePortId: order.dischargePortId?.value ?? NaN,
            cargoUnitId: stringToNumOpt(order.cargoUnitId) ?? null,
            orderName: order.orderName || null,
          }),
        );
        const persistedOrderById = mapKeyBy(
          persistedValues?.orders ?? [],
          (o) => o.id,
        );

        /** Handle potential need to map any invalid ports or commodities to other ones */
        const synonymMappingOrderPairs = filteredMap(parsedOrders, (order) => {
          if (typeof order.id !== 'number') return NONE;
          const existingOrder = persistedOrderById.get(order.id);
          if (!existingOrder) return NONE;
          const res: [
            UseSynonymMappingsOrderWithData,
            UseSynonymMappingsOrder,
          ] = [existingOrder, order];
          return res;
        });
        const { mapLocations, mapAssortments } = await getSynonymMappings(
          synonymMappingOrderPairs,
        );

        onSave(
          parsedVoyageCharter,
          parsedOrders,
          { mapLocations, mapAssortments },
          opts,
        );
      } catch (err) {
        console.error('Error parsing saved voyage charter', err);
        setErrorMessage(err.toString());
      }
    };

  const handleContractChange = (newContract: ContractDetailsSchema) => {
    setContract((curContract) => {
      if (newContract.type === 'coa' && newContract.coaId) {
        const currentValues = getValues();
        const coa = coas.find((c) => c.id === newContract.coaId);

        if (!coa) {
          console.error('Selected COA not found', newContract.coaId);
          return curContract;
        }

        const charterer = coa.organizationByChartererOrgId;
        const shipowner = coa.organizationByShipownerOrgId;
        const currency = coa.currency;
        const cargoUnitId = coa.amountUnitId;
        const coaRouteIds = new Set(
          compact(coa.coaContractPortsByCoaContractId.nodes).map(
            (coaPort) => coaPort.id,
          ),
        );

        if (charterer) {
          setValue('chartererOrgId', {
            value: charterer.id,
            label: charterer.name ?? '',
          });
        }

        if (shipowner) {
          setValue('shipownerOrgId', {
            value: shipowner.id,
            label: shipowner.name ?? '',
          });
        }

        if (currency) {
          setValue('currency', currency);
        }

        if (cargoUnitId) {
          const currentOrders = currentValues.relatedOrders;

          setValue(
            'relatedOrders',
            currentOrders.map(({ order, coaRouteId, ...rest }) => ({
              ...rest,
              coaRouteId:
                coaRouteId !== null && coaRouteIds.has(coaRouteId.value)
                  ? coaRouteId
                  : null,
              order: {
                ...order,
                cargoUnitId,
              },
            })),
          );
        }
      }

      return newContract;
    });
  };

  const handleFirstLoadPortChange: OnFirstLoadPortChange = (location) => {
    setTimezone(location.timezone ?? 'UTC');

    if (isDefaultLaycanEnabled && location.timezone) {
      const { layday, cancelling } = getValues();

      let newLayday = layday;
      let newCancelling = cancelling;

      if (!layday) {
        newLayday = moment.tz(location.timezone).startOf('day').toDate();
      }

      if (!cancelling && newLayday) {
        newCancelling = moment(newLayday)
          .tz(location.timezone)
          .add(DEFAULT_LAYCAN_DAYS - 1, 'days')
          .endOf('day')
          .toDate();
      }

      setValue('layday', newLayday);
      setValue('cancelling', newCancelling);
    }
  };

  const onPressIn = useEvent((e) => {
    if (!formRef.current?.checkValidity()) {
      e.preventDefault();
      setTimeout(() => {
        formRef.current?.reportValidity();
      }, 0);
    }
  });

  const formSettingsName:
    | 'voyage_charter_coa'
    | 'voyage_charter_inquiry'
    | 'voyage_charter_spot' = `voyage_charter_${contract.type}`;

  const apiOrderNoticeByOrderId = useMemo(() => {
    const notices = new Map<number, string>();

    const { relatedOrders } = getValues();
    const persistedOrderById = mapKeyBy(
      persistedValues?.orders ?? [],
      (o) => o.id,
    );

    relatedOrders.forEach(({ order }) => {
      if (typeof order.id !== 'number') return NONE;
      const persistedOrder = persistedOrderById.get(order.id);
      if (!persistedOrder) return NONE;
      const notice = persistedOrder.apiOrdersNoticesByOrderId.nodes[0]?.notices;
      if (!notice) return NONE;
      notices.set(order.id, notice);
    });

    return notices;
  }, [getValues, persistedValues?.orders]);

  return (
    <Box display="flex" flexDirection="column" height="100%">
      <FormSettingsProvider form={formSettingsName}>
        <Form ref={formRef} flex={1} overflow="scroll">
          {validationErrors.length > 0 && (
            <Box
              backgroundColor={'#e74c03'}
              color={'white'}
              paddingX={'14px'}
              paddingY={'16px'}
              position="absolute"
              top="20px"
              right="28px"
              width="300px"
              zIndex="2000"
            >
              <Heading level={2} fontSize={3} fontWeight="bold">
                Please Correct the Following Errors Before Submitting:
              </Heading>
              <Box display="flex" flexDirection="column" marginTop="6px">
                {validationErrors.map((error, index) => (
                  <Box marginTop="4px" key={index}>
                    {error}
                  </Box>
                ))}
              </Box>
            </Box>
          )}
          <Heading level={1} p={4} fontSize={4} fontWeight="bold">
            {title}
          </Heading>
          {loading ? (
            <LoadingSpinner />
          ) : (
            <>
              <ContractTypeSelect
                coas={coas}
                contract={contract}
                defaultContract={null}
                onChange={handleContractChange}
              />
              {persistedValues?.voyageCharter.relatedOrders
                .map((order) => order.order.externalOrderId)
                .filter(isDefined)
                .map((externalOrderId) => (
                  <OrderEmailInfo
                    externalOrderId={externalOrderId}
                    key={externalOrderId}
                  />
                ))}
              <VoyageCharterOrderFields
                isCoa={isCoa}
                assortments={assortments}
                coaAssortments={coaAssortmentOptions}
                cargoUnits={cargoUnits}
                coaRoutes={coaRoutes}
                coa={selectedCoa}
                coaValues={inheritedCoaValues}
                defaultCargoUnitId={defaultCargoUnitId}
                enableCoaRouteSelect={coaId !== null}
                form={form}
                getCoaFreight={getCoaFreight}
                onFirstLoadPortChange={handleFirstLoadPortChange}
                orders={orders}
              />
              <BaseContractFields
                form={form}
                shipowners={shipowners}
                charterers={charterers}
                vessels={filteredVessels}
                refetch={refetch}
                timezone={timezone}
                currencyErrorMessage={currencyErrorMessage}
              />
              <VoyageCharterFormFreights
                assortments={assortments}
                cargoUnits={cargoUnits}
                defaultValues={defaultValues}
                form={form}
                freightUnitErrorMessage={freightUnitErrorMessage}
                apiOrderNoticeByOrderId={apiOrderNoticeByOrderId}
                getCoaFreight={getCoaFreight}
                allLocationsData={allLocationsData}
              />
              <VoyageCharterFormLaytimeTerms
                coaValues={inheritedCoaValues}
                defaultPortData={defaultValues.portData}
                form={form}
              />
              <ExtraFreightFields form={form} extraFreights={[]} />
              <ExtraCostFields form={form} extraCosts={[]} />
              <CommissionFields
                form={form}
                organizations={organizations}
                refetch={refetch}
                assortments={filterCommissionAssortments}
                coaRoutes={coaRoutes}
                enableCoaRouteSelect={coaId !== null}
                coaCommissions={coaCommissions}
                defaultCommissions={defaultValues.commissions} // commission created in voyage charter
              />
            </>
          )}
        </Form>
        <ModalFooter>
          {onClose && (
            <Button variant="secondary" mr={3} onPress={onClose}>
              Cancel
            </Button>
          )}
          {persistedValues && (
            <Button
              disabled={isSaveLoading}
              variant="negative"
              mr={3}
              onPress={onDelete}
            >
              Delete
            </Button>
          )}

          <Button
            aria-label="save and close"
            disabled={isSaveLoading}
            onMouseDown={onPressIn}
            onPress={handleSubmit(onSubmit({ shouldClose: true }), oninvalid)}
            mr={3}
          >
            Save & Close
          </Button>

          <Button
            disabled={isSaveLoading}
            mr={3}
            onMouseDown={onPressIn}
            onPress={handleSubmit(
              onSubmit({ shouldClose: true, showSuggestions: true }),
              oninvalid,
            )}
          >
            Save and show suggestions
          </Button>
          <div style={{ flexGrow: 1 }} />
          <Text color="status.error" fontWeight="bold">
            {errorMessage}
          </Text>
          <FormSettingsDropdown />
        </ModalFooter>
      </FormSettingsProvider>
    </Box>
  );
}

interface ContractTypeSelectProps {
  coas: VoyageCharterForm_CoaFragment[];
  contract: VoyageCharterFormContractDetails;
  defaultContract: ContractDetailsSchema | null;
  onChange: (contract: VoyageCharterFormContractDetails) => void;
}

export function ContractTypeSelect({
  coas,
  contract,
  defaultContract,
  onChange,
}: ContractTypeSelectProps): JSX.Element {
  const coaOptions = useMemo(
    () =>
      coas.map((coa) => ({
        value: coa.id,
        label: `${coa.name} (Charterer: ${
          coa.organizationByChartererOrgId?.name
        }) - End Date: ${
          coa.endDate ? new Date(coa.endDate).toLocaleDateString() : 'N/A'
        }${coa.externalId ? ` - Ext ID: ${coa.externalId}` : ''}`,
      })),
    [coas],
  );

  const onContractTypeChange = (type: ContractDetailsSchema['type']) => {
    switch (type) {
      case 'coa': {
        onChange({
          type,
          coaId: defaultContract?.type === 'coa' ? defaultContract.coaId : null,
          isPreliminary:
            defaultContract?.type === 'coa'
              ? defaultContract.isPreliminary
              : false,
        });
        break;
      }
      case 'spot': {
        onChange({
          type,
          onSubsUntil:
            defaultContract?.type === 'spot'
              ? defaultContract.onSubsUntil
              : null,
        });
        break;
      }
      case 'inquiry': {
        onChange({
          type,
        });
        break;
      }
      default: {
        exhaustiveCheck(type);
      }
    }
  };

  const onOnSubsUntilChange = (newOnSubsUntil: Date | null) => {
    if (contract.type !== 'spot') return;

    onChange({
      ...contract,
      onSubsUntil: newOnSubsUntil?.toISOString() ?? null,
    });
  };

  const onCoaChange = (opt: { value: number; label: string } | null) => {
    if (opt) {
      onChange({
        type: 'coa',
        coaId: opt.value,
        isPreliminary: contract.type === 'coa' ? contract.isPreliminary : false,
      });
    }
  };

  const onIsPreliminaryChange = (newValue: boolean) => {
    if (contract.type !== 'coa') return;

    onChange({
      ...contract,
      isPreliminary: newValue,
    });
  };

  return (
    <FormSection title="Contract">
      <FormRow>
        <FormField label="Contract type" width={250}>
          <Box display="flex" flexDirection="column">
            <label>
              <input
                data-testid="radio-inquiry"
                type="radio"
                name="contractType"
                value="inquiry"
                onChange={(e) =>
                  e.target.checked && onContractTypeChange('inquiry')
                }
                checked={contract.type === 'inquiry'}
              />{' '}
              Inquiry
            </label>
            <label>
              <input
                data-testid="radio-coa"
                type="radio"
                name="contractType"
                value="coa"
                onChange={(e) =>
                  e.target.checked && onContractTypeChange('coa')
                }
                checked={contract.type === 'coa'}
              />{' '}
              COA
            </label>
            <label>
              <input
                data-testid="radio-spot"
                type="radio"
                name="contractType"
                value="spot"
                onChange={(e) =>
                  e.target.checked && onContractTypeChange('spot')
                }
                checked={contract.type === 'spot'}
              />{' '}
              Spot
            </label>
          </Box>
        </FormField>
        {contract.type === 'coa' && (
          <Box>
            <FormField
              data-testid="select-coa-dropdown"
              label="Select COA"
              width={510}
            >
              <ReactSelect
                options={coaOptions}
                value={coaOptions.find((opt) => opt.value === contract.coaId)}
                onChange={onCoaChange}
                placeholder="Select COA"
                styles={reactSelectStyles}
              />
            </FormField>
            <FormField label="Options">
              <Box
                as="label"
                htmlFor="is_preliminary"
                display="inline-flex"
                alignItems="center"
              >
                <input
                  id="is_preliminary"
                  type="checkbox"
                  checked={contract.type === 'coa' && contract.isPreliminary}
                  onChange={(e) =>
                    onIsPreliminaryChange(e.currentTarget.checked)
                  }
                />
                <Text isUnselectable ml={2}>
                  Preliminary
                </Text>
              </Box>
            </FormField>
          </Box>
        )}
        {contract.type === 'spot' && (
          <Box>
            <FormRow>
              <FormField label="On subjects until" width={200}>
                <DatePicker
                  showTimeInput
                  onChange={onOnSubsUntilChange}
                  selected={
                    contract.type === 'spot' && contract.onSubsUntil
                      ? new Date(contract.onSubsUntil)
                      : null
                  }
                  timeZone="UTC"
                  isClearable
                />
              </FormField>
              <FormField width={200}>
                <Button
                  variant="secondary"
                  onPress={() => onOnSubsUntilChange(null)}
                >
                  Clear
                </Button>
              </FormField>
            </FormRow>
          </Box>
        )}
      </FormRow>
    </FormSection>
  );
}

type GetCoaFreight = (relatedOrder: RelatedOrderSchema) => {
  result: {
    amount: number;
    unit: PaymentUnit;
    currency: Currency;
  } | null;
  errors: {
    invalidAmount: boolean;
  } | null;
};

type OnFirstLoadPortChange = (location: {
  id: number;
  name: string;
  timezone: string | null;
}) => void;

type CoaAssortmentOption = {
  value: number;
  isStowageFactor: boolean;
  label: string;
  amountRanges: { min: number | null; max: number | null }[];
};

interface VoyageCharterOrderFieldsProps {
  assortments: VoyageCharterForm_AssortmentFragment[];
  coaAssortments: CoaAssortmentOption[];
  cargoUnits: VoyageCharterForm_CargoUnitFragment[];
  coaRoutes: VoyageCharterForm_CoaRouteFragment[];
  coa: VoyageCharterForm_CoaFragment | null;
  coaValues: CoaValues | null;
  defaultCargoUnitId: number | null;
  enableCoaRouteSelect: boolean;
  form: UseFormReturn<VoyageCharterFormSchema>;
  isCoa: boolean;
  getCoaFreight: GetCoaFreight;
  onFirstLoadPortChange: OnFirstLoadPortChange;
  orders: VoyageCharter_OrderFragment[];
}

function getCoaRouteOption(
  coaRoute: VoyageCharterForm_CoaRouteFragment,
): CoaRouteOption {
  return {
    value: coaRoute.id,
    label: `${
      coaRoute.locationByOriginId?.name ?? coaRoute.zoneByOriginZoneId?.name
    } → ${
      coaRoute.locationByDestinationId?.name ??
      coaRoute.zoneByDestinationZoneId?.name
    }`,
    loadPortId: coaRoute.originId ?? null,
    loadPortName: coaRoute.locationByOriginId?.name ?? 'Load port name N/A',
    loadZoneId: coaRoute.originZoneId ?? null,
    loadZoneName: coaRoute.zoneByOriginZoneId?.name ?? 'Load zone name N/A',
    loadPortTimezone: coaRoute.locationByOriginId?.timezone ?? null,
    dischargePortId: coaRoute.destinationId ?? null,
    dischargePortName:
      coaRoute.locationByDestinationId?.name ?? 'Discharge port name N/A',
    dischargeZoneId: coaRoute.destinationZoneId ?? null,
    dischargeZoneName:
      coaRoute.zoneByDestinationZoneId?.name ?? 'Discharge zone name N/A',
  };
}

function VoyageCharterOrderFields({
  assortments,
  coaAssortments,
  cargoUnits,
  coa,
  coaRoutes,
  coaValues,
  defaultCargoUnitId,
  enableCoaRouteSelect,
  form,
  isCoa,
  getCoaFreight,
  onFirstLoadPortChange,
  orders,
}: VoyageCharterOrderFieldsProps): JSX.Element {
  const { control } = form;
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'relatedOrders',
    keyName: 'key',
  });

  const { effectiveRatesByOrderIndex } = useEffectiveLoadingRates(
    form,
    coaValues,
    assortments,
  );

  const assortmentOptions = useMemo(
    () =>
      assortments
        .map((assortment) => ({
          value: assortment.id,
          label: assortment.name,
          isStowageFactor:
            !!assortment.stowageFactorMin || !!assortment.stowageFactorMax,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [assortments],
  );

  const cargoExclusionOptions = useMemo(
    () =>
      assortments.map((ag) => ({
        value: ag?.id ?? NaN,
        label: ag?.name ?? 'UNNAMED CARGO EXCLUSION',
      })) ?? [],
    [assortments],
  );

  const cargoUnitOptions = useMemo(
    () =>
      cargoUnits
        .map((cargoUnit) => ({
          value: cargoUnit.id,
          label: cargoUnit.name ?? '',
          name: cargoUnit.name ?? '',
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [cargoUnits],
  );

  const coaRouteOptions = useMemo(
    () =>
      coaRoutes
        .map(getCoaRouteOption)
        .sort((a, b) => a.label.localeCompare(b.label)),
    [coaRoutes],
  );

  function appendOrder(): RelatedOrderSchema {
    const firstOrder = form.getValues('relatedOrders').at(0);

    const defaultValues = getDefaultOrder({
      assortmentId: assortmentOptions.at(0)?.value ?? 0,
      cargoUnitId: defaultCargoUnitId,
    });

    if (!firstOrder) {
      return defaultValues;
    }

    const copiedOrder = structuredClone(firstOrder);

    return {
      ...copiedOrder,
      order: {
        ...copiedOrder.order,
        id: defaultValues.order.id,
        externalOrderId: defaultValues.order.externalOrderId,
        orderName: defaultValues.order.orderName,
        extraInfo: defaultValues.order.extraInfo,
      },
      relationId: defaultValues.relationId,
      freightId: defaultValues.freightId,
    };
  }

  return (
    <FormSection title="Cargo orders/parcels" display="block" muted>
      {fields.map((orderField, index) => (
        <OrderRow
          key={orderField.key}
          assortments={assortments}
          assortmentOptions={assortmentOptions}
          coaAssortmentOptions={coaAssortments}
          cargoExclusionOptions={cargoExclusionOptions}
          cargoUnitOptions={cargoUnitOptions}
          coaRouteOptions={coaRouteOptions}
          coaCargoUnit={
            coa?.cargoUnitByAmountUnitId?.name ?? FALLBACK_COA_CARGO_UNIT
          }
          effectiveLoadingRates={effectiveRatesByOrderIndex.get(index) ?? null}
          enableCoaRouteSelect={enableCoaRouteSelect}
          form={form}
          getCoaFreight={getCoaFreight}
          index={index}
          onFirstLoadPortChange={onFirstLoadPortChange}
          onRemove={() => remove(index)}
          orderId={orderField.order.id}
          isCoa={isCoa}
          orders={orders}
        />
      ))}
      <Button
        variant="secondary"
        onPress={() => {
          append(appendOrder(), {
            focusName: `relatedOrders.${fields.length}.order.cargoAmount`,
          });
        }}
      >
        Add cargo order/parcel
      </Button>
    </FormSection>
  );
}

interface CoaRouteOption {
  value: number;
  label: string;
  loadPortId: number | null;
  loadPortName: string | null;
  loadPortTimezone: string | null;
  loadZoneId: number | null;
  loadZoneName: string | null;
  dischargePortId: number | null;
  dischargePortName: string | null;
  dischargeZoneId: number | null;
  dischargeZoneName: string | null;
}

interface OrderRowProps {
  assortments: VoyageCharterForm_AssortmentFragment[];
  assortmentOptions: (AutoCompleteOption & { isStowageFactor: boolean })[];
  coaAssortmentOptions: CoaAssortmentOption[];
  coaCargoUnit: string | null;
  cargoExclusionOptions: SelectOption[];
  cargoUnitOptions: SelectOption[];
  coaRouteOptions: CoaRouteOption[];
  effectiveLoadingRates: OrderEffectiveLoadingRates | null;
  enableCoaRouteSelect: boolean;
  index: number;
  form: UseFormReturn<VoyageCharterFormSchema>;
  isCoa: boolean;
  getCoaFreight: GetCoaFreight;
  onFirstLoadPortChange: OnFirstLoadPortChange;
  onRemove: () => void;
  orderId: number | string;
  orders: VoyageCharter_OrderFragment[];
}

type OrderEffectiveLoadingRates = {
  loading: EffectiveLoadingRate | null;
  loadingRateParams: GetEffectiveLoadingRateParams;
  discharge: EffectiveLoadingRate | null;
  dischargeRateParams: GetEffectiveLoadingRateParams;
};

export const useEffectiveLoadingRates = (
  form: UseFormReturn<VoyageCharterFormSchema>,
  coaValues: CoaValues | null,
  assortments: VoyageCharterForm_AssortmentFragment[],
) => {
  const { control } = form;

  const watchedOrders = useWatch({
    control,
    name: 'relatedOrders',
  });

  const watchedPortData = useWatch({
    control,
    name: 'portData',
  });

  const effectiveLoadingRates = useMemo(() => {
    const assortmentById = mapKeyBy(assortments, (a) => a.id);

    const effectiveRatesByOrderIndex = new Map<
      number,
      OrderEffectiveLoadingRates
    >();

    for (const [index, relatedOrder] of watchedOrders.entries()) {
      const loadPortId = relatedOrder.order.loadPortId?.value;
      const dischargePortId = relatedOrder.order.dischargePortId?.value;

      const loadPortCoaPortData = loadPortId
        ? coaValues?.coaPortDataByLocationId.get(loadPortId)
        : null;
      const loadPortVcPortData = loadPortId
        ? watchedPortData[getPrefixedNumberKey(loadPortId)]
        : null;
      const dischargePortCoaPortData = dischargePortId
        ? coaValues?.coaPortDataByLocationId.get(dischargePortId)
        : null;
      const dischargePortVcPortData = dischargePortId
        ? watchedPortData[getPrefixedNumberKey(dischargePortId)]
        : null;

      const assortment = assortmentById.get(relatedOrder.order.assortmentId);

      const loadingRateParams = {
        order: {
          rate: relatedOrder.order.loadingRate ?? null,
          unit: relatedOrder.order.loadingUnit ?? null,
        },
        voyageCharter: loadPortVcPortData
          ? {
              rate: loadPortVcPortData.loadingRate,
              unit: loadPortVcPortData.loadingDischargeUnit,
            }
          : null,
        coa: loadPortCoaPortData
          ? {
              rate: withDefined(loadPortCoaPortData.loadingRate, Number),
              unit: loadPortCoaPortData.loadingDischargeUnit ?? null,
            }
          : null,
        assortment: assortment
          ? {
              rate: withDefined(assortment.loadingRate, Number),
              unit: assortment.loadingUnit ?? null,
            }
          : null,
        port: null,
        vessel: null,
        portCommodity: null,
      };

      const dischargeRateParams = {
        order: {
          rate: relatedOrder.order.dischargingRate ?? null,
          unit: relatedOrder.order.dischargingUnit ?? null,
        },
        voyageCharter: dischargePortVcPortData
          ? {
              rate: dischargePortVcPortData.dischargeRate,
              unit: dischargePortVcPortData.loadingDischargeUnit,
            }
          : null,
        coa: dischargePortCoaPortData
          ? {
              rate: withDefined(dischargePortCoaPortData.dischargeRate, Number),
              unit: dischargePortCoaPortData.loadingDischargeUnit ?? null,
            }
          : null,
        assortment: assortment
          ? {
              rate: withDefined(assortment.dischargingRate, Number),
              unit: assortment.dischargingUnit ?? null,
            }
          : null,
        port: null,
        vessel: null,
        portCommodity: null,
      };

      const effectiveLoadingRate = getEffectiveLoadingRate(loadingRateParams);

      const effectiveDischargeRate =
        getEffectiveLoadingRate(dischargeRateParams);

      effectiveRatesByOrderIndex.set(index, {
        loading: effectiveLoadingRate.rate ? effectiveLoadingRate : null,
        loadingRateParams,
        discharge: effectiveDischargeRate.rate ? effectiveDischargeRate : null,
        dischargeRateParams,
      });
    }

    return {
      effectiveRatesByOrderIndex,
    };
  }, [
    assortments,
    coaValues?.coaPortDataByLocationId,
    watchedOrders,
    watchedPortData,
  ]);

  return effectiveLoadingRates;
};

function formatEffectiveLoadingRate(rate: EffectiveLoadingRate) {
  return `${rate.rate} ${loadingUnitToName[rate.unit]} (${loadingRateSourceToLabel[rate.source]})`;
}

function OrderRow({
  assortments,
  assortmentOptions,
  coaAssortmentOptions,
  coaCargoUnit,
  cargoExclusionOptions,
  cargoUnitOptions,
  coaRouteOptions,
  effectiveLoadingRates,
  enableCoaRouteSelect,
  index,
  form,
  getCoaFreight,
  onFirstLoadPortChange,
  onRemove,
  orderId,
  isCoa,
  orders,
}: OrderRowProps): JSX.Element {
  const { openModal } = useModal();
  const {
    control,
    getValues,
    register,
    setValue,
    watch,
    formState: { errors },
  } = form;

  const currentOrder = typeof orderId === 'number' ? orders[index] : undefined;

  const currentLoadPort = currentOrder?.locationByLoadPortId
    ? currentOrder.locationByLoadPortId
    : undefined;
  const currentLoadPortTimezone = currentLoadPort?.timezone
    ? currentLoadPort.timezone
    : undefined;

  const currentDischargePort = currentOrder?.locationByDischargePortId
    ? currentOrder.locationByDischargePortId
    : undefined;
  const currentDischargePortTimezone = currentDischargePort?.timezone
    ? currentDischargePort.timezone
    : undefined;

  const currentUser = useCurrentUser();
  const shouldUseReducedUnits =
    currentUser?.rootOrganization?.configReducedUnits ?? false;
  const [isCoaAmountInvalid, setIsCoaAmountInvalid] = useState(false);

  const [loadPortTimezone, setLoadPortTimezone] = useState<string | undefined>(
    currentLoadPortTimezone ?? 'UTC',
  );
  const [dischargePortTimezone, setDischargePortTimezone] = useState<
    string | undefined
  >(currentDischargePortTimezone ?? 'UTC');

  const checkCoaFreightErrors = () => {
    const currentValues = getValues();
    const currentOrderValues = currentValues.relatedOrders[index];

    const { errors } = getCoaFreight(currentOrderValues);

    setIsCoaAmountInvalid(errors?.invalidAmount ?? false);
  };

  useEffect(
    () => {
      checkCoaFreightErrors();
    },
    // We only want to check this once as otherwise the validation is done on
    // user input so we don't put anything to deps to avoid unnecessary checks
    //
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const onCoaRouteChange = (newRoute: CoaRouteOption | null) => {
    const currentValues = getValues();
    const newRelatedOrders = [...currentValues.relatedOrders];

    if (newRoute && newRoute.loadPortId && newRoute.loadPortName) {
      newRelatedOrders[index] = {
        ...newRelatedOrders[index],
        order: {
          ...newRelatedOrders[index].order,
          loadPortId: {
            value: newRoute.loadPortId,
            label: newRoute.loadPortName,
          },
        },
      };

      onFirstLoadPortChange({
        id: newRoute.loadPortId,
        name: newRoute.loadPortName,
        timezone: newRoute.loadPortTimezone,
      });
    }

    if (newRoute && newRoute.dischargePortId && newRoute.dischargePortName) {
      newRelatedOrders[index] = {
        ...newRelatedOrders[index],
        order: {
          ...newRelatedOrders[index].order,
          dischargePortId: {
            value: newRoute.dischargePortId,
            label: newRoute.dischargePortName,
          },
        },
      };
    }

    newRelatedOrders[index] = {
      ...newRelatedOrders[index],
      coaRouteId: newRoute,
    };

    setValue('relatedOrders', newRelatedOrders);
  };

  const onOrderView = () => {
    if (isClientId(orderId)) {
      return;
    }

    openModal({
      type: 'VIEW_ORDER',
      props: {
        orderId,
        disableDelete: true,
      },
    });
  };

  const watchedOrders = watch('relatedOrders');

  const watchedAssortmentId = watchedOrders[index].order.assortmentId;
  const coaAssortmentOption = coaAssortmentOptions.find(
    ({ value }) => value === watchedAssortmentId,
  );
  const coaAssortmentOptionValue = coaAssortmentOption?.value;

  const [coaAssortmentId, setCoaAssortmentId] = useState(
    coaAssortmentOptionValue ? watchedAssortmentId : 0,
  );
  const [assortmentId, setAssortmentId] = useState(
    isCoa
      ? !coaAssortmentOptionValue
        ? watchedAssortmentId
        : 0
      : watchedAssortmentId,
  );

  const contractLoadingRate =
    effectiveLoadingRates?.loadingRateParams.voyageCharter?.rate &&
    effectiveLoadingRates.loadingRateParams.voyageCharter.unit
      ? effectiveLoadingRates.loadingRateParams.voyageCharter
      : effectiveLoadingRates?.loadingRateParams.coa?.rate &&
          effectiveLoadingRates.loadingRateParams.coa.unit
        ? effectiveLoadingRates.loadingRateParams.coa
        : null;

  const hasLoadingRateOverride =
    effectiveLoadingRates?.loading?.source === 'order' &&
    contractLoadingRate &&
    (effectiveLoadingRates.loading.rate !== contractLoadingRate.rate ||
      effectiveLoadingRates.loading.unit !== contractLoadingRate.unit);

  const contractDischargeRate =
    effectiveLoadingRates?.dischargeRateParams.voyageCharter?.rate &&
    effectiveLoadingRates.dischargeRateParams.voyageCharter.unit
      ? effectiveLoadingRates.dischargeRateParams.voyageCharter
      : effectiveLoadingRates?.dischargeRateParams.coa?.rate &&
          effectiveLoadingRates.dischargeRateParams.coa.unit
        ? effectiveLoadingRates.dischargeRateParams.coa
        : null;

  const hasDischargeRateOverride =
    effectiveLoadingRates?.discharge?.source === 'order' &&
    contractDischargeRate &&
    (effectiveLoadingRates.discharge.rate !== contractDischargeRate.rate ||
      effectiveLoadingRates.discharge.unit !== contractDischargeRate.unit);

  return (
    <>
      <FormRow borderBottom="dashed 1px rgba(0, 0, 0, 0.41)" mb={3} pb={3}>
        <Input
          type="hidden"
          {...register(`relatedOrders.${index}.order.assortmentId`, {
            required: true,
          })}
        />
        <FormField
          data-testid="cargo-form-orders-all-commodities"
          label={enableCoaRouteSelect ? 'All commodities' : 'Commodity'}
          width={220}
          required={!coaAssortmentId}
        >
          <AutoCompleteSelect
            isDisabled={false}
            options={assortmentOptions}
            value={assortmentId}
            onChange={(value) => {
              const newValue = value ?? 0;
              setAssortmentId(newValue);
              setCoaAssortmentId(0);
              setValue(`relatedOrders.${index}.order.assortmentId`, newValue);
              checkCoaFreightErrors();
            }}
          />
        </FormField>
        {enableCoaRouteSelect && (
          <FormField
            data-testid="cargo-form-orders-coa-commodities"
            label="COA commodities"
            width={220}
            required={!assortmentId}
          >
            <AutoCompleteSelect
              isDisabled={false}
              options={coaAssortmentOptions}
              value={coaAssortmentId}
              onChange={(value) => {
                const newValue = value ?? 0;
                setCoaAssortmentId(newValue);
                setAssortmentId(0);
                setValue(`relatedOrders.${index}.order.assortmentId`, newValue);
                checkCoaFreightErrors();
              }}
            />
          </FormField>
        )}
        <FormField
          data-testid="cargo-form-orders-quantity"
          label="Quantity"
          width={120}
          required
          hasError={
            !!errors.relatedOrders?.[index]?.order?.cargoAmount ||
            isCoaAmountInvalid
          }
          helpText={
            isCoaAmountInvalid ? (
              <Tooltip
                content={
                  <Box>
                    <strong>Allowed quantities</strong>
                    {coaAssortmentOption?.amountRanges.map((range) => {
                      const minLabel = `${range.min || 0}`;
                      const maxLabel =
                        range.max !== null ? `- ${range.max}` : '';

                      return (
                        <Box key={`${minLabel}${maxLabel}`}>
                          {minLabel} {maxLabel} {coaCargoUnit}
                        </Box>
                      );
                    })}
                  </Box>
                }
              >
                <span>
                  <AlertIcon width={12} height={12} /> Quantity not on COA
                </span>
              </Tooltip>
            ) : undefined
          }
        >
          <Input
            type="number"
            required
            step="0.001"
            {...register(`relatedOrders.${index}.order.cargoAmount`, {
              setValueAs: stringToNumber,
              onBlur: () => checkCoaFreightErrors(),
            })}
          />
        </FormField>
        <FormField label="Unit" width={90} required>
          <Select
            data-testid="cargo-form-orders-unit"
            backgroundColor="white"
            options={cargoUnitOptions}
            required
            {...register(`relatedOrders.${index}.order.cargoUnitId`, {
              setValueAs: stringToNumber,
              onChange: () => checkCoaFreightErrors(),
            })}
          />
        </FormField>

        <FormRow>
          {enableCoaRouteSelect && (
            <FormField
              label="COA Route"
              data-testid="cargo-form-orders-coa-routes"
              width={220}
            >
              <Controller
                control={control}
                name={`relatedOrders.${index}.coaRouteId`}
                defaultValue={undefined}
                render={({ field: { value } }) => (
                  <ReactSelect
                    name="coaRoutes"
                    options={coaRouteOptions}
                    placeholder="Select route"
                    value={value}
                    onChange={(newValue: CoaRouteOption | null) => {
                      onCoaRouteChange(newValue);
                      checkCoaFreightErrors();
                    }}
                    styles={reactSelectStyles}
                    isClearable
                  />
                )}
                rules={{ required: true }}
              />
            </FormField>
          )}
          <FormField
            data-testid="cargo-form-orders-load-port"
            label="Load port"
            width={220}
            required
            hasError={!!errors.relatedOrders?.[index]?.order?.loadPortId}
          >
            <Controller
              defaultValue={undefined}
              control={control}
              name={`relatedOrders.${index}.order.loadPortId`}
              render={({ field: { value, onChange, name } }) => (
                <LocationSelect
                  value={value ?? undefined}
                  name={name}
                  required={true}
                  onChange={(newValue) => {
                    if (!newValue) {
                      setValue(`relatedOrders.${index}.order.loadPortId`, null);
                      return;
                    }

                    onChange(newValue);
                    checkCoaFreightErrors();

                    if (index === 0 && newValue?.location?.id != null) {
                      onFirstLoadPortChange({
                        id: newValue.location.id,
                        name: newValue.location.name ?? 'N/A',
                        timezone: newValue.location?.timezone ?? null,
                      });
                    }
                    if (newValue) {
                      const timezone = newValue.location?.timezone ?? 'UTC';
                      setLoadPortTimezone(timezone);
                    }
                  }}
                />
              )}
              rules={{ required: true }}
            />
          </FormField>
          <FormField
            data-testid="cargo-form-orders-discharge-port"
            label="Discharge port"
            width={220}
            required
            hasError={!!errors.relatedOrders?.[index]?.order?.dischargePortId}
          >
            <Controller
              defaultValue={undefined}
              control={control}
              name={`relatedOrders.${index}.order.dischargePortId`}
              render={({ field: { value, onChange, name } }) => (
                <LocationSelect
                  value={value ?? undefined}
                  name={name}
                  required={true}
                  onChange={(newValue) => {
                    if (!newValue) {
                      setValue(
                        `relatedOrders.${index}.order.dischargePortId`,
                        null,
                      );
                      return;
                    }
                    onChange(newValue);
                    checkCoaFreightErrors();
                    if (newValue) {
                      const timezone = newValue.location?.timezone ?? 'UTC';
                      setDischargePortTimezone(timezone);
                    }
                  }}
                />
              )}
              rules={{ required: true }}
            />
          </FormField>
        </FormRow>

        <FormRow>
          <FormField
            data-testid="cargo-form-orders-order-name"
            label="Order Name"
            width={220}
            hasError={!!errors.relatedOrders?.[index]?.order?.orderName}
            visibilityId="order_name"
          >
            <Input {...register(`relatedOrders.${index}.order.orderName`)} />
          </FormField>
          <FormField
            data-testid="cargo-form-orders-other-remarks"
            label="Other remarks"
            width={448}
            visibilityId="extra_info"
          >
            <Input {...register(`relatedOrders.${index}.order.extraInfo`)} />
          </FormField>
        </FormRow>
        <FormRow>
          <FormField
            data-testid="cargo-form-orders-loading-rate"
            label="Loading rate"
            width={220}
            hasError={!!errors.relatedOrders?.[index]?.order?.loadingRate}
            visibilityId="loading_rate"
            notifications={
              hasLoadingRateOverride ? (
                <OverrideTooltip
                  message={formatLoadingRateOverrideTooltip(
                    effectiveLoadingRates.loadingRateParams,
                  )}
                />
              ) : null
            }
          >
            <Input
              {...register(`relatedOrders.${index}.order.loadingRate`, {
                setValueAs: stringToNumber,
              })}
              type="number"
              step="any"
              placeholder={
                effectiveLoadingRates?.loading
                  ? formatEffectiveLoadingRate(effectiveLoadingRates.loading)
                  : undefined
              }
            />
          </FormField>
          <FormField
            data-testid="cargo-form-orders-loading-unit"
            label="Loading unit"
            width={220}
            visibilityId="loading_rate"
          >
            <Select
              {...register(`relatedOrders.${index}.order.loadingUnit`, {
                setValueAs: valueOrNull,
              })}
              options={loadingUnitOptions(shouldUseReducedUnits)}
              backgroundColor="white"
              enablePlaceholder
              placeholder="-"
            />
          </FormField>

          <FormField
            data-testid="cargo-form-orders-discharge-rate"
            label="Discharging rate"
            width={220}
            hasError={!!errors.relatedOrders?.[index]?.order?.dischargingRate}
            visibilityId="discharge_rate"
            notifications={
              hasDischargeRateOverride ? (
                <OverrideTooltip
                  message={formatLoadingRateOverrideTooltip(
                    effectiveLoadingRates.dischargeRateParams,
                  )}
                />
              ) : null
            }
          >
            <Input
              {...register(`relatedOrders.${index}.order.dischargingRate`, {
                setValueAs: stringToNumber,
              })}
              type="number"
              step="any"
              placeholder={
                effectiveLoadingRates?.discharge
                  ? formatEffectiveLoadingRate(effectiveLoadingRates.discharge)
                  : undefined
              }
            />
          </FormField>
          <FormField
            data-testid="cargo-form-orders-discharge-unit"
            label="Discharging unit"
            width={220}
            visibilityId="discharge_rate"
          >
            <Select
              {...register(`relatedOrders.${index}.order.dischargingUnit`, {
                setValueAs: valueOrNull,
              })}
              options={loadingUnitOptions(shouldUseReducedUnits)}
              backgroundColor="white"
              enablePlaceholder
              placeholder="-"
            />
          </FormField>
        </FormRow>
        <FormRow>
          <OrderRowLaycanInputs
            form={form}
            loadPortTimezone={loadPortTimezone}
            orderIndex={index}
          />
          <FormField
            label="Earliest discharging"
            data-testid="cargo-form-orders-earliest-discharging"
            width={220}
            helpText={dischargePortTimezone}
            hasError={!!errors.relatedOrders?.[index]?.order?.rta}
            visibilityId="earliest_discharging"
          >
            <Controller
              defaultValue={null}
              control={control}
              name={`relatedOrders.${index}.order.rta`}
              render={({ field: { value, onChange } }) => (
                <DatePicker
                  showTimeInput
                  onChange={onChange}
                  selected={value}
                  timeZone={dischargePortTimezone}
                />
              )}
            />
          </FormField>
          <FormField
            label="Latest discharging"
            data-testid="cargo-form-orders-latest-discharging"
            width={220}
            helpText={dischargePortTimezone}
            hasError={!!errors.relatedOrders?.[index]?.order?.deliveredBefore}
            visibilityId="earliest_discharging"
          >
            <Controller
              defaultValue={null}
              control={control}
              name={`relatedOrders.${index}.order.deliveredBefore`}
              render={({ field: { value, onChange } }) => (
                <DatePicker
                  showTimeInput
                  onChange={onChange}
                  selected={value}
                  defaultTimePickerType="END_OF_DAY"
                  timeZone={dischargePortTimezone}
                />
              )}
            />
          </FormField>
        </FormRow>
        <FormRow>
          <FormField
            data-testid="cargo-form-orders-loading-overhead"
            label="Overhead for loading (hrs)"
            width={220}
            hasError={
              !!errors.relatedOrders?.[index]?.order?.loadingOverheadDuration
            }
            visibilityId="loading_overhead_duration"
          >
            <Input
              {...register(
                `relatedOrders.${index}.order.loadingOverheadDuration`,
                {
                  setValueAs: stringToNumber,
                },
              )}
              type="number"
              step="any"
            />
          </FormField>
          <FormField
            data-testid="cargo-form-orders-discharging-overhead"
            label="Overhead for discharging (hrs)"
            width={220}
            hasError={
              !!errors.relatedOrders?.[index]?.order
                ?.dischargingOverheadDuration
            }
            visibilityId="discharging_overhead_duration"
          >
            <Input
              {...register(
                `relatedOrders.${index}.order.dischargingOverheadDuration`,
                {
                  setValueAs: stringToNumber,
                },
              )}
              type="number"
              step="any"
            />
          </FormField>
        </FormRow>
        <FormRow>
          <FormField
            label="Deck cargo"
            data-testid="cargo-form-orders-deck-cargo"
            width={448}
            hasError={!!errors.relatedOrders?.[index]?.order?.cargoDeckOption}
            visibilityId="external_order_id"
          >
            <Controller
              control={control}
              name={`relatedOrders.${index}.order.cargoDeckOption`}
              render={({ field: { value, onChange } }) => (
                <Box
                  display="flex"
                  flexDirection="row"
                  alignItems="center"
                  height="100%"
                >
                  <label style={{ marginRight: '16px' }}>
                    <input
                      type="radio"
                      onChange={() => onChange('DECK')}
                      checked={value === 'DECK'}
                    />{' '}
                    Must be stowed on deck
                  </label>
                  <label style={{ marginRight: '16px' }}>
                    <input
                      type="radio"
                      onChange={() => onChange('HOLDS')}
                      checked={value === 'HOLDS'}
                    />{' '}
                    Must be stowed in holds
                  </label>
                  <label>
                    <input
                      type="radio"
                      onChange={() => onChange('DECK_OR_HOLDS')}
                      checked={value === 'DECK_OR_HOLDS'}
                    />{' '}
                    Can be stowed on deck or in holds
                  </label>
                </Box>
              )}
            />
          </FormField>
          <FormField
            label="Fixture note"
            data-testid="cargo-form-orders-fixture-note"
            width={220}
            hasError={!!errors.relatedOrders?.[index]?.order?.fixtureNote}
            visibilityId="external_order_id"
          >
            <Input
              disabled={true}
              {...register(`relatedOrders.${index}.order.fixtureNote`)}
            />
          </FormField>
          <FormField
            label="External System ID"
            data-testid="cargo-form-orders-external-system-ID"
            width={220}
            hasError={!!errors.relatedOrders?.[index]?.order?.externalOrderId}
            visibilityId="external_order_id"
          >
            <Input
              disabled={true}
              {...register(`relatedOrders.${index}.order.externalOrderId`)}
            />
          </FormField>
        </FormRow>
        <CalculationBoxes
          assortments={assortments}
          index={index}
          orderId={orderId}
          onOrderView={onOrderView}
          onRemove={onRemove}
          register={register}
          control={control}
          errors={errors}
          assortmentOptions={assortmentOptions}
          cargoExclusionOptions={cargoExclusionOptions}
          cargoUnitOptions={cargoUnitOptions}
          effectiveLoadingRates={effectiveLoadingRates}
        />
      </FormRow>
    </>
  );
}

function OrderRowLaycanInputs({
  form,
  loadPortTimezone,
  orderIndex,
}: {
  form: UseFormReturn<VoyageCharterFormSchema>;
  loadPortTimezone: string | undefined;
  orderIndex: number;
}) {
  const {
    control,
    getValues,
    setValue,
    formState: { errors },
  } = form;

  const watchedContractLayday = useWatch({
    name: 'layday',
    control,
    defaultValue: null,
  });
  const watchedContractCancelling = useWatch({
    name: 'cancelling',
    control,
    defaultValue: null,
  });
  const watchedOrderLayday = useWatch({
    name: `relatedOrders.${orderIndex}.order.layday`,
    control,
    defaultValue: null,
  });
  const watchedOrderCancelling = useWatch({
    name: `relatedOrders.${orderIndex}.order.cancelling`,
    control,
    defaultValue: null,
  });

  const hasLaydayOverride =
    !!watchedOrderLayday &&
    !!watchedContractLayday &&
    watchedOrderLayday.valueOf() !== watchedContractLayday.valueOf();
  const hasCancellingOverride =
    !!watchedOrderCancelling &&
    !!watchedContractCancelling &&
    watchedOrderCancelling.valueOf() !== watchedContractCancelling.valueOf();

  const onLaycanChange = (changedField: 'layday' | 'cancelling') => {
    if (!loadPortTimezone) return;

    const { layday, cancelling } = getValues(
      `relatedOrders.${orderIndex}.order`,
    );

    if (changedField === 'layday' && layday) {
      setValue(
        `relatedOrders.${orderIndex}.order.cancelling`,
        moment(layday)
          .tz(loadPortTimezone)
          .add(DEFAULT_LAYCAN_DAYS - 1, 'days')
          .endOf('day')
          .toDate(),
      );
    }

    if (
      changedField === 'cancelling' &&
      cancelling &&
      (!layday || !moment(cancelling).isAfter(moment(layday)))
    ) {
      setValue(
        `relatedOrders.${orderIndex}.order.layday`,
        moment(cancelling)
          .tz(loadPortTimezone)
          .subtract(DEFAULT_LAYCAN_DAYS - 1, 'days')
          .startOf('day')
          .toDate(),
      );
    }
  };

  return (
    <>
      <FormField
        label="Earliest loading"
        data-testid="cargo-form-orders-earliest-loading"
        width={220}
        helpText={loadPortTimezone}
        hasError={!!errors.relatedOrders?.[orderIndex]?.order?.layday}
        visibilityId="earliest_loading"
        notifications={
          hasLaydayOverride && watchedContractLayday ? (
            <OverrideTooltip
              message={`Current value overrides Layday set on Contract basics. Overridden value: ${timeFieldFormat(watchedContractLayday.valueOf(), loadPortTimezone)}`}
            />
          ) : null
        }
      >
        <Controller
          defaultValue={null}
          control={control}
          name={`relatedOrders.${orderIndex}.order.layday`}
          render={({ field: { value, onChange } }) => (
            <DatePicker
              showTime
              showTimeInput
              onChange={(newValue) => {
                onChange(newValue);
                onLaycanChange('layday');
              }}
              selected={value}
              defaultTimePickerType="START_OF_DAY"
              timeZone={loadPortTimezone}
            />
          )}
        />
      </FormField>
      <FormField
        label="Latest loading"
        data-testid="cargo-form-orders-latest-loading"
        width={220}
        helpText={loadPortTimezone}
        hasError={!!errors.relatedOrders?.[orderIndex]?.order?.cancelling}
        visibilityId="earliest_loading"
        notifications={
          hasCancellingOverride && watchedContractCancelling ? (
            <OverrideTooltip
              message={`Current value overrides Cancelling set on Contract basics. Overridden value: ${timeFieldFormat(watchedContractCancelling.valueOf(), loadPortTimezone)}`}
            />
          ) : null
        }
      >
        <Controller
          defaultValue={null}
          control={control}
          name={`relatedOrders.${orderIndex}.order.cancelling`}
          render={({ field: { value, onChange } }) => (
            <DatePicker
              showTime
              showTimeInput
              onChange={(newValue) => {
                onChange(newValue);
                onLaycanChange('cancelling');
              }}
              selected={value}
              defaultTimePickerType="END_OF_DAY"
              timeZone={loadPortTimezone}
            />
          )}
        />
      </FormField>
    </>
  );
}

function CalculationBoxes(props: {
  assortments: VoyageCharterForm_AssortmentFragment[];
  index: number;
  orderId: string | number;
  onOrderView: () => void;
  onRemove: () => void;
  register: UseFormRegister<VoyageCharterFormSchema>;
  control: Control<VoyageCharterFormSchema>;
  errors: FieldErrors<VoyageCharterFormSchema>;
  assortmentOptions: (AutoCompleteOption & { isStowageFactor: boolean })[];
  cargoExclusionOptions: SelectOption[];
  cargoUnitOptions: SelectOption[];
  effectiveLoadingRates: OrderEffectiveLoadingRates | null;
}) {
  const {
    index,
    orderId,
    onOrderView,
    onRemove,
    register,
    control,
    errors,
    cargoExclusionOptions,
    cargoUnitOptions,
    effectiveLoadingRates,
  } = props || {};

  const [isOptionsOpen, setOptionsOpen] = useState(false);
  const watchedOrder = useWatch({
    control,
    name: `relatedOrders.${index}.order`,
  });

  const watchedAssortmentId = watchedOrder.assortmentId;
  const assortment = props.assortments.find(
    (a) => a.id === watchedAssortmentId,
  );

  const watchedAssortmentOptions = watchedOrder.assortmentOptions;

  const cargoUnit = cargoUnitOptions.find(
    (c) => c.value === watchedOrder?.cargoUnitId,
  );
  const currentExclusions = watchedAssortmentOptions?.cargoExclusions;
  const cargoAmount = watchedOrder?.cargoAmount;

  const marginAmount =
    watchedAssortmentOptions?.marginAmount ?? assortment?.marginAmount;
  const marginUnit =
    watchedAssortmentOptions?.marginUnit ?? assortment?.marginUnit;
  const specificGravity = watchedAssortmentOptions?.specificGravity;
  const stowageFactorMin = watchedAssortmentOptions?.stowageFactor?.min;
  const stowageFactorMax = watchedAssortmentOptions?.stowageFactor?.max;

  const {
    cargoRanges,
    minLoadingTime,
    maxLoadingTime,
    minDischargingTime,
    maxDischargingTime,
  } = useMemo(() => {
    let cargoRanges: CargoRanges = {
      mass: { min: 0, max: 0 },
      volume: { min: 0, max: 0 },
    };

    if (cargoUnit) {
      cargoRanges = calculateMassAndVolumeRanges(
        {
          assortmentByAssortmentId: assortment,
          cargoUnitByCargoUnitId: cargoUnit as CargoUnit,
          cargoAmount,
          marginAmount,
          marginUnit,
          specificGravity,
          stowageFactorMin,
          stowageFactorMax,
        },
        false,
      );
    }

    const minLoadingTime = getLoadingTime(
      cargoRanges.mass.min,
      cargoRanges.volume.min,
      effectiveLoadingRates?.loading?.rate ?? null,
      effectiveLoadingRates?.loading?.unit ?? null,
    );

    const maxLoadingTime = getLoadingTime(
      cargoRanges.mass.max,
      cargoRanges.volume.max,
      effectiveLoadingRates?.loading?.rate ?? null,
      effectiveLoadingRates?.loading?.unit ?? null,
    );

    const minDischargingTime = getLoadingTime(
      cargoRanges.mass.min,
      cargoRanges.volume.min,
      effectiveLoadingRates?.discharge?.rate ?? null,
      effectiveLoadingRates?.discharge?.unit ?? null,
    );

    const maxDischargingTime = getLoadingTime(
      cargoRanges.mass.max,
      cargoRanges.volume.max,
      effectiveLoadingRates?.discharge?.rate ?? null,
      effectiveLoadingRates?.discharge?.unit ?? null,
    );

    return {
      cargoRanges,
      minLoadingTime,
      maxLoadingTime,
      minDischargingTime,
      maxDischargingTime,
    };
  }, [
    assortment,
    cargoUnit,
    cargoAmount,
    marginAmount,
    marginUnit,
    specificGravity,
    stowageFactorMin,
    stowageFactorMax,
    effectiveLoadingRates?.discharge?.rate,
    effectiveLoadingRates?.discharge?.unit,
    effectiveLoadingRates?.loading?.rate,
    effectiveLoadingRates?.loading?.unit,
  ]);

  return (
    <>
      <FormVisibilityBox visibilityId="calculation-box">
        <FormRow marginTop={2}>
          <CalculationBox minWidth="448px">
            <Box display="flex" flexDirection="row" flexGrow={1}>
              <Box display="flex" flexDirection="column" flexGrow={1}>
                <div>Min loading time</div>
                <div>{minLoadingTime.toFixed(1)} h</div>
              </Box>
              <Box display="flex" flexDirection="column" flexGrow={1}>
                <div>Max loading time</div>
                <div>{maxLoadingTime.toFixed(1)} h</div>
              </Box>
            </Box>
          </CalculationBox>

          <Box width="0.2em" />
          <CalculationBox minWidth="448px">
            <Box display="flex" flexDirection="row" flexGrow={1}>
              <Box display="flex" flexDirection="column" flexGrow={1}>
                <div>Min discharging time</div>
                <div>{minDischargingTime.toFixed(1)} h</div>
              </Box>
              <Box display="flex" flexDirection="column" flexGrow={1}>
                <div>Max discharging time</div>
                <div>{maxDischargingTime.toFixed(1)} h</div>
              </Box>
            </Box>
          </CalculationBox>
        </FormRow>
      </FormVisibilityBox>

      <FormRow>
        <Box display="flex" flexDirection="row">
          <FormVisibilityBox visibilityId="commodity-options-toggle">
            <Button
              onPress={() => setOptionsOpen(!isOptionsOpen)}
              variant="secondary"
              mr={2}
            >
              Stowage details
              {isOptionsOpen ? UPCARET : DOWNCARET}{' '}
            </Button>
          </FormVisibilityBox>
          {!isClientId(orderId) && (
            <Button variant="secondary" onPress={onOrderView} mr={2}>
              View
            </Button>
          )}
          <Button variant="secondary" onPress={onRemove}>
            Remove
          </Button>
        </Box>
      </FormRow>

      {/*Commodity options box*/}
      <Box
        borderWidth="1px"
        minWidth="905px"
        borderStyle="solid"
        borderColor="input.border"
        backgroundColor="white"
        padding={4}
        marginTop={3}
        display={isOptionsOpen ? undefined : 'none'}
      >
        <FormRow>
          <FormField
            label="Specific gravity"
            data-testid="cargo-form-stowage-details-specific-gravity"
            width={220}
            hasError={
              !!errors.relatedOrders?.[index]?.order?.assortmentOptions
                ?.specificGravity
            }
          >
            <Input
              {...register(
                `relatedOrders.${index}.order.assortmentOptions.specificGravity`,
                {
                  setValueAs: stringToNumber,
                },
              )}
              step="any"
              type="number"
              placeholder={assortment?.specificGravity?.toString()}
            />
          </FormField>
          <FormField
            label="Stowage factor MIN (ft³&#8202;/&#8202;LT)"
            data-testid="cargo-form-stowage-details-stowage-factor-min"
            width={220}
            hasError={
              !!errors.relatedOrders?.[index]?.order?.assortmentOptions
                ?.stowageFactor?.min
            }
          >
            <Input
              {...register(
                `relatedOrders.${index}.order.assortmentOptions.stowageFactor.min`,
                {
                  setValueAs: stringToNumber,
                },
              )}
              type="number"
              placeholder={assortment?.stowageFactorMin?.toString()}
            />
          </FormField>
          <FormField
            label="Stowage factor MAX (ft³&#8202;/&#8202;LT)"
            data-testid="cargo-form-stowage-details-stowage-factor-max"
            width={220}
            hasError={
              !!errors.relatedOrders?.[index]?.order?.assortmentOptions
                ?.stowageFactor?.max
            }
          >
            <Input
              {...register(
                `relatedOrders.${index}.order.assortmentOptions.stowageFactor.max`,
                {
                  setValueAs: stringToNumber,
                },
              )}
              type="number"
              placeholder={assortment?.stowageFactorMax?.toString()}
            />
          </FormField>
        </FormRow>

        <FormRow>
          <FormField
            label="Margin"
            data-testid="cargo-form-stowage-details-margin"
            width={220}
            hasError={
              !!errors.relatedOrders?.[index]?.order?.assortmentOptions
                ?.marginAmount
            }
          >
            <Input
              {...register(
                `relatedOrders.${index}.order.assortmentOptions.marginAmount`,
                {
                  setValueAs: stringToNumber,
                },
              )}
              type="number"
              placeholder={assortment?.marginAmount?.toString()}
            />
          </FormField>
          <FormField
            label="Margin unit"
            data-testid="cargo-form-stowage-details-margin-unit"
            width={220}
            hasError={
              !!errors.relatedOrders?.[index]?.order?.assortmentOptions
                ?.marginUnit
            }
          >
            <Select
              {...register(
                `relatedOrders.${index}.order.assortmentOptions.marginUnit`,
              )}
              backgroundColor="white"
              options={marginUnits}
              placeholder={
                (assortment?.marginUnit &&
                  marginUnits.find((mu) => mu.id === assortment.marginUnit)
                    ?.label) ||
                ''
              }
            />
          </FormField>

          <FormField
            label="IMO class"
            width={220}
            data-testid="cargo-form-stowage-details-imo-class"
          >
            <Select
              {...register(
                `relatedOrders.${index}.order.assortmentOptions.imoClass`,
                {
                  setValueAs: valueOrNull,
                },
              )}
              options={imoClassOptions}
              placeholder="Select IMO class"
              backgroundColor="white"
            />
          </FormField>
        </FormRow>

        <FormRow marginTop={3} marginBottom={3}>
          <CalculationBox>
            <Box display="flex" flexDirection="row" flexGrow={1}>
              <Box display="flex" flexDirection="column" flexGrow={1}>
                <div>Min volume</div>
                <div>{cargoRanges.volume.min.toFixed(2)} m³</div>
              </Box>
              <Box display="flex" flexDirection="column" flexGrow={1}>
                <div>Max volume</div>
                <div>{cargoRanges.volume.max.toFixed(2)} m³</div>
              </Box>
            </Box>
          </CalculationBox>

          <Box width="1em" />

          <CalculationBox>
            <Box display="flex" flexDirection="row" flexGrow={1}>
              <Box display="flex" flexDirection="column" flexGrow={1}>
                <div>Min mass</div>
                <div>{cargoRanges.mass.min.toFixed(2)} mts</div>
              </Box>
              <Box display="flex" flexDirection="column" flexGrow={1}>
                <div>Max mass</div>
                <div>{cargoRanges.mass.max.toFixed(2)} mts</div>
              </Box>
            </Box>
          </CalculationBox>
        </FormRow>

        <FormRow>
          <FormField
            label="Previous cargo exclusions"
            data-testid="cargo-form-stowage-details-previous-cargo-exclusions"
            hasError={
              !!errors.relatedOrders?.[index]?.order?.assortmentOptions
                ?.cargoExclusions
            }
          >
            <Controller
              control={control}
              name={`relatedOrders.${index}.order.assortmentOptions.cargoExclusions`}
              defaultValue={[]}
              render={({ field: { onChange, value } }) => (
                <ReactSelect
                  name="cargoExclusions"
                  options={cargoExclusionOptions}
                  placeholder="Select cargo exclusions"
                  isMulti
                  value={value?.map((sel) =>
                    cargoExclusionOptions.find((eo) => eo.value === sel.value),
                  )}
                  onChange={(value) => onChange(value)}
                  styles={reactSelectStyles}
                />
              )}
            />
          </FormField>
          <FormField
            label="Exclusion required for"
            width={250}
            display={
              !currentExclusions || currentExclusions.length === 0
                ? 'none'
                : undefined
            }
            hasError={
              !!errors.relatedOrders?.[index]?.order?.assortmentOptions
                ?.cargoExclusionAmount
            }
          >
            <Input
              {...register(
                `relatedOrders.${index}.order.assortmentOptions.cargoExclusionAmount`,
                {
                  setValueAs: stringToNumber,
                },
              )}
              type="number"
              required={!!(currentExclusions && currentExclusions.length > 0)}
            />
          </FormField>

          <FormField
            label="Exclusion amount type"
            display={
              !currentExclusions || currentExclusions.length === 0
                ? 'none'
                : undefined
            }
            width={250}
          >
            <Select
              {...register(
                `relatedOrders.${index}.order.assortmentOptions.cargoExclusionType`,
              )}
              options={cargoExclusionTypeOptions}
              placeholder="Select cargo exclusion type"
              required={!!(currentExclusions && currentExclusions.length > 0)}
            />
          </FormField>
        </FormRow>
      </Box>
    </>
  );
}

function calculateTotalFreight(
  order: CargoRangeInputs,
  freightAmount: number,
  freightUnit: PaymentUnit,
  worldscaleFreightRates: WorldscaleFreightRateMap,
  worldscaleOrderData: OrderPorts,
) {
  const {
    cargoUnitByCargoUnitId,
    cargoAmount,
    specificGravity,
    stowageFactorMin,
    stowageFactorMax,
  } = order;

  const cargoRanges = calculateMassAndVolumeRanges(
    {
      cargoUnitByCargoUnitId: cargoUnitByCargoUnitId,
      cargoAmount: cargoAmount,
      marginAmount: 0,
      marginUnit: null,
      specificGravity: specificGravity,
      stowageFactorMin: stowageFactorMin,
      stowageFactorMax: stowageFactorMax,
    },
    false,
  );

  const mass = midPoint(cargoRanges.mass);
  const volume = midPoint(cargoRanges.volume);

  const pcs =
    cargoUnitByCargoUnitId?.name === 'pcs' ||
    cargoUnitByCargoUnitId?.name === 'set'
      ? Number(cargoAmount)
      : null;

  const freightCost = getFreightCost(
    mass,
    volume,
    pcs,
    freightAmount,
    freightUnit,
    worldscaleFreightRates,
    worldscaleOrderData,
  );

  return freightCost;
}

function VoyageCharterFormFreights({
  assortments,
  cargoUnits,
  defaultValues,
  form,
  freightUnitErrorMessage,
  apiOrderNoticeByOrderId,
  getCoaFreight,
  allLocationsData,
}: {
  assortments: VoyageCharterForm_AssortmentFragment[];
  cargoUnits: VoyageCharterForm_CargoUnitFragment[];
  defaultValues: VoyageCharterFormSchema;
  form: UseFormReturn<VoyageCharterFormSchema>;
  freightUnitErrorMessage: string | null;
  apiOrderNoticeByOrderId: Map<number, string>;
  getCoaFreight: GetCoaFreight;
  allLocationsData: AllLocationsQuery | undefined;
}): JSX.Element {
  const { control, register, watch } = form;
  const [worldscaleErrors, setWorldscaleErrors] = useState<Map<number, string>>(
    new Map(),
  );

  const currentUser = useCurrentUser();
  const organizationId = currentUser?.organizationId;
  const configEnableWorldscaleFreights =
    currentUser?.organization?.configEnableWorldscaleFreights ?? false;

  const { data: worldscaleData, refetch: refetchWorldscale } =
    useAllWorldscaleFreightsQuery({
      variables: {
        filter: {
          organizationId: { equalTo: organizationId },
        },
      },
      skip: !organizationId || !configEnableWorldscaleFreights,
    });

  useEffect(() => {
    if (organizationId && configEnableWorldscaleFreights) {
      refetchWorldscale();
    }
  }, [organizationId, configEnableWorldscaleFreights, refetchWorldscale]);

  const worldscaleFreightRates = useMemo(
    () =>
      buildWorldscaleFreightRateMap(
        worldscaleData?.allWorldscaleFreights?.nodes,
      ),
    [worldscaleData],
  );

  const watchedLumpsum = useWatch({
    control,
    name: 'lumpsum',
    defaultValue: null,
  });

  const watchedOrders = watch('relatedOrders', defaultValues.relatedOrders);
  const currency = form.watch('currency');
  const freights = getFreights(watchedOrders);
  const isFreightDisabled = !!watchedLumpsum;

  const coaFreightByOrderIndex = useMemo(() => {
    return watchedOrders.map((order) => {
      const { result } = getCoaFreight(order);
      return result;
    });
  }, [watchedOrders, getCoaFreight]);

  const filteredPaymentUnits = useMemo(() => {
    if (configEnableWorldscaleFreights) {
      return paymentUnits;
    }
    return paymentUnits.filter((unit) => unit.value !== PaymentUnit.WORLDSCALE);
  }, [configEnableWorldscaleFreights]);

  useEffect(() => {
    if (!configEnableWorldscaleFreights) {
      setWorldscaleErrors(new Map());
      return;
    }

    const newErrors = new Map<number, string>();

    watchedOrders.forEach((order, index) => {
      if (order.freightUnit === PaymentUnit.WORLDSCALE) {
        const loadPortName = order.order.loadPortId?.label;
        const dischargePortName = order.order.dischargePortId?.label;

        if (loadPortName && dischargePortName && worldscaleData) {
          const worldscaleFreights =
            worldscaleData.allWorldscaleFreights?.nodes || [];
          const hasWorldscaleFreight = worldscaleFreights.some(
            (freight) =>
              freight?.locationByFromLocationId?.name === loadPortName &&
              freight?.locationByToLocationId?.name === dischargePortName,
          );

          if (!hasWorldscaleFreight) {
            newErrors.set(
              index,
              "Couldn't find Worldscale flatrate for the route.",
            );
          }
        }
      }
    });

    setWorldscaleErrors(newErrors);
  }, [watchedOrders, worldscaleData, configEnableWorldscaleFreights]);

  const totalFreightsByOrderIndex = useMemo(() => {
    const results = new Map<number, number | null>();

    watchedOrders.forEach((order, orderIndex) => {
      const currentFreight = freights.get(orderIndex);
      if (!currentFreight) return null;
      if (!order.freightAmount || !order.freightUnit) {
        results.set(orderIndex, null);
        return;
      }

      const cargoUnit = cargoUnits.find(
        (unit) => unit.id === currentFreight.order.cargoUnitId,
      );
      const assortment = assortments.find(
        (assortment) => assortment.id === currentFreight.order.assortmentId,
      );

      const loadPort = allLocationsData?.allLocations?.nodes.find(
        (loc) => loc?.id === currentFreight.order.loadPortId?.value,
      );
      const dischargePort = allLocationsData?.allLocations?.nodes.find(
        (loc) => loc?.id === currentFreight.order.dischargePortId?.value,
      );

      const worldscaleOrderData: OrderPorts = {
        locationByLoadPortId: loadPort
          ? {
              name: loadPort.name,
              country: loadPort.country,
            }
          : null,
        locationByDischargePortId: dischargePort
          ? {
              name: dischargePort.name,
              country: dischargePort.country,
            }
          : null,
      };

      const totalFreight =
        order.freightAmount && order.freightUnit
          ? calculateTotalFreight(
              {
                cargoUnitByCargoUnitId: cargoUnit,
                cargoAmount: currentFreight.order.cargoAmount,
                marginAmount: 0,
                marginUnit: null,
                specificGravity: assortment?.specificGravity,
                stowageFactorMin: assortment?.stowageFactorMin,
                stowageFactorMax: assortment?.stowageFactorMax,
              },
              order.freightAmount,
              order.freightUnit,
              worldscaleFreightRates,
              worldscaleOrderData,
            )
          : null;

      results.set(orderIndex, totalFreight);
    });

    return results;
  }, [
    watchedOrders,
    freights,
    cargoUnits,
    assortments,
    allLocationsData?.allLocations?.nodes,
    worldscaleFreightRates,
  ]);

  return (
    <FormSection title="Freights" muted>
      {watchedOrders.length === 0 ? (
        <Text mt={2}>No freight has been added yet.</Text>
      ) : (
        watchedOrders.map((order, orderIndex) => {
          const currentFreight = freights.get(orderIndex);
          if (!currentFreight) return null;

          const orderId = currentFreight.order.id;
          const rawApiNotice = orderId
            ? apiOrderNoticeByOrderId.get(Number(orderId))
            : null;
          const apiNotice = rawApiNotice ? JSON.parse(rawApiNotice) : null;
          const missingFreightUnit = apiNotice?.missing_freight_unit;
          const coaFreight = coaFreightByOrderIndex.at(orderIndex);
          const isCoaFreightOverridden =
            coaFreight &&
            order.freightAmount &&
            (order.freightAmount !== coaFreight.amount ||
              order.freightUnit !== coaFreight.unit);

          const coaFreightPlaceholder = coaFreight
            ? `${formatCurrency(coaFreight.amount, 2, coaFreight.currency)} ${
                paymentUnitMap[coaFreight.unit].label
              } (COA)`
            : undefined;

          const totalFreight =
            totalFreightsByOrderIndex.get(orderIndex) ?? null;
          const totalFreightPlaceholder = `${formatCurrency(totalFreight, 0, currency)}`;

          return (
            <FormRow key={`${order.order.id}`}>
              <FormField label="Commodity" width={200}>
                <Text mt={2}>
                  {assortments.find(
                    (a) => a.id === currentFreight?.order.assortmentId,
                  )?.name ?? ''}
                </Text>
              </FormField>
              <FormField label="Amount" width={200}>
                <Text mt={2}>
                  {currentFreight.order.cargoAmount}{' '}
                  {
                    cargoUnits.find(
                      (cu) => cu.id === currentFreight.order.cargoUnitId,
                    )?.name
                  }
                </Text>
              </FormField>
              <FormField label="Load port" width={200}>
                <Text mt={2}>
                  <Text mt={2}>
                    {currentFreight.order.loadPortId?.label ?? ''}
                  </Text>
                </Text>
              </FormField>
              <FormField label="Discharge port" width={200}>
                <Text mt={2}>
                  {currentFreight.order.dischargePortId?.label ?? ''}
                </Text>
              </FormField>
              <FormField
                label="Freight"
                data-testid="cargo-form-freights-freight"
                width={200}
                notifications={
                  isCoaFreightOverridden && coaFreightPlaceholder ? (
                    <OverrideTooltip
                      message={`Current value overrides freight rate defined on COA. Overridden value: ${coaFreightPlaceholder}`}
                    />
                  ) : null
                }
              >
                {isFreightDisabled ? (
                  <Text mt={2}>(Total contract freight is used)</Text>
                ) : (
                  <Input
                    step="0.01"
                    type="number"
                    {...register(`relatedOrders.${orderIndex}.freightAmount`, {
                      setValueAs: stringToNumber,
                    })}
                    placeholder={coaFreightPlaceholder}
                  />
                )}
              </FormField>
              <FormField
                label="Freight Unit"
                data-testid="cargo-form-freights-freight-unit"
                width={200}
              >
                {!isFreightDisabled && (
                  <Select
                    {...register(`relatedOrders.${orderIndex}.freightUnit`, {
                      setValueAs: valueOrNull,
                    })}
                    options={paymentUnitsToOptions(filteredPaymentUnits)}
                    enablePlaceholder
                  />
                )}
                {freightUnitErrorMessage && (
                  <Text mt={2} color="red">
                    {freightUnitErrorMessage}
                  </Text>
                )}
                {missingFreightUnit && (
                  <Text mt={2} fontSize={12} color="red">
                    Missing - Check the integration
                  </Text>
                )}
                {worldscaleErrors.get(orderIndex) && (
                  <Text mt={2} fontSize={12} color="red">
                    {worldscaleErrors.get(orderIndex)}
                  </Text>
                )}
              </FormField>
              {totalFreightPlaceholder && (
                <FormField
                  label="Total Freight"
                  data-testid="cargo-form-freights-total-freight"
                  width={200}
                  ml={5}
                >
                  <Text mt={2}>{totalFreightPlaceholder}</Text>
                </FormField>
              )}
            </FormRow>
          );
        })
      )}
    </FormSection>
  );
}

function getRelatedOrderLocationNamesById(relatedOrders: RelatedOrderSchema[]) {
  const map = new Map<number, { id: number; name: string }>();

  relatedOrders.forEach(({ order }) => {
    if (order.loadPortId) {
      map.set(order.loadPortId.value, {
        id: order.loadPortId.value,
        name: order.loadPortId.label,
      });
    }

    if (order.dischargePortId) {
      map.set(order.dischargePortId.value, {
        id: order.dischargePortId.value,
        name: order.dischargePortId.label,
      });
    }
  });

  return map;
}

/** Removes port data entries for removed ports */
export function removeLeftoverPortData(
  portData: VoyageCharterFormSchema['portData'],
  relatedOrders: RelatedOrderSchema[],
) {
  const locationNameById = getRelatedOrderLocationNamesById(relatedOrders);

  return pickBy(portData, (_pd, key) => {
    const portId = parsePrefixedNumberKey(key);
    return locationNameById.has(portId);
  });
}

function VoyageCharterFormLaytimeTerms({
  coaValues,
  defaultPortData,
  form,
}: {
  coaValues: CoaValues | null;
  defaultPortData: VoyageCharterFormSchema['portData'];
  form: UseFormReturn<VoyageCharterFormSchema>;
}) {
  const watchedOrders = form.watch('relatedOrders') ?? [];
  const locationNameById = getRelatedOrderLocationNamesById(watchedOrders);

  return (
    <FormSection title="Laytime terms">
      <FormProvider {...form}>
        <PortDataForm
          defaultValues={defaultPortData ?? {}}
          isCompressed={true}
          isReadonly={false}
          portAgents={[]}
          locationById={locationNameById}
          inheritedCoaValues={coaValues}
        />
      </FormProvider>
    </FormSection>
  );
}
